import { useState, useEffect, createContext, useContext, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Auth Context
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// About Us Page
const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800">
      <Navigation />
      
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <img 
            src="/origin-logo.png" 
            alt="Origin Audio" 
            className="h-24 mx-auto mb-8 object-contain"
          />
          <h1 className="text-6xl font-bold text-white mb-6" data-testid="about-title">
            Reclaiming the Value of Music
          </h1>
          <p className="text-2xl text-gray-300 max-w-3xl mx-auto">
            Origin Audio is the Direct-to-Fan marketplace that fixes the broken math of streaming. 
            Where artists keep 90% and fans truly own their music.
          </p>
        </div>

        {/* The Problem */}
        <div className="max-w-5xl mx-auto mb-20">
          <div className="bg-red-900/20 border border-red-700 rounded-2xl p-10">
            <h2 className="text-4xl font-bold text-white mb-6 text-center">The Streaming Trap</h2>
            <div className="grid md:grid-cols-2 gap-8 mb-6">
              <div className="text-center">
                <div className="text-6xl font-bold text-red-400 mb-2">3,000</div>
                <p className="text-gray-300">Streams needed to buy a $15 pizza</p>
              </div>
              <div className="text-center">
                <div className="text-6xl font-bold text-red-400 mb-2">$0.003</div>
                <p className="text-gray-300">Average per stream payout</p>
              </div>
            </div>
            <p className="text-gray-300 text-center text-lg">
              The current streaming model treats music as a utility. For artists, it's their life's work.
            </p>
          </div>
        </div>

        {/* Our Solution */}
        <div className="max-w-5xl mx-auto mb-20">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">The Origin Audio Solution</h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-600 rounded-xl p-8 text-center">
              <div className="text-5xl mb-4">💰</div>
              <h3 className="text-2xl font-bold text-green-400 mb-3">90/10 Split</h3>
              <p className="text-gray-300">
                Artists keep 90% of every sale. One album sale = the net profit of 3,000 streams.
              </p>
            </div>

            <div className="bg-gradient-to-br from-cyan-900/30 to-cyan-800/20 border border-cyan-600 rounded-xl p-8 text-center">
              <div className="text-5xl mb-4">🎵</div>
              <h3 className="text-2xl font-bold text-cyan-400 mb-3">True Ownership</h3>
              <p className="text-gray-300">
                Fans don't rent access—they own high-fidelity files they can keep forever.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border border-purple-600 rounded-xl p-8 text-center">
              <div className="text-5xl mb-4">📊</div>
              <h3 className="text-2xl font-bold text-purple-400 mb-3">Data Sovereignty</h3>
              <p className="text-gray-300">
                Artists own their fan relationships and data—no platform lock-in.
              </p>
            </div>
          </div>

          {/* Referral Program Highlight */}
          <div className="bg-gradient-to-br from-yellow-600/30 to-yellow-800/30 border-2 border-yellow-500 rounded-2xl p-10 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 text-9xl opacity-10">💰</div>
            <div className="relative z-10">
              <div className="inline-block bg-yellow-500 text-black px-6 py-2 rounded-full font-bold text-sm mb-4">
                NEW: ARTIST REFERRAL PROGRAM
              </div>
              <h3 className="text-4xl font-bold text-yellow-400 mb-4">
                Earn 1% of Every Sale. FOREVER.
              </h3>
              <p className="text-2xl text-yellow-200 mb-6 max-w-3xl mx-auto">
                Refer other artists to Origin Audio and earn <strong className="text-yellow-400">1% lifetime commission</strong> on every sale they make. Build passive income while helping artists escape the streaming trap!
              </p>
              <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                <div className="bg-black/30 rounded-lg p-4">
                  <div className="text-3xl font-bold text-yellow-400">1%</div>
                  <div className="text-yellow-200 text-sm">Lifetime Commission</div>
                </div>
                <div className="bg-black/30 rounded-lg p-4">
                  <div className="text-3xl font-bold text-yellow-400">♾️</div>
                  <div className="text-yellow-200 text-sm">Forever Income</div>
                </div>
                <div className="bg-black/30 rounded-lg p-4">
                  <div className="text-3xl font-bold text-yellow-400">∞</div>
                  <div className="text-yellow-200 text-sm">Unlimited Referrals</div>
                </div>
              </div>
              <p className="text-yellow-300 mt-6 text-sm">
                Example: Refer 10 artists who each make $10,000/year = <strong>$1,000/year passive income!</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="max-w-5xl mx-auto mb-20">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">Platform Features</h2>
          
          <div className="space-y-8">
            {/* The Vault */}
            <div className="bg-gray-900/80 border border-gray-700 rounded-xl p-8">
              <div className="flex items-start space-x-6">
                <div className="text-5xl">🎵</div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-cyan-400 mb-3">The Vault</h3>
                  <p className="text-gray-300 mb-4">
                    High-fidelity lossless audio streaming (WAV, FLAC, MP3). Your personal music library, accessible anywhere. 
                    Convenience meets quality—stream your purchased music from the cloud without storage limits.
                  </p>
                  <ul className="text-gray-400 space-y-2">
                    <li>• Lossless audio quality</li>
                    <li>• Stream from anywhere</li>
                    <li>• Full playback controls</li>
                    <li>• Offline download capability</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Digital Tangibles */}
            <div className="bg-gray-900/80 border border-gray-700 rounded-xl p-8">
              <div className="flex items-start space-x-6">
                <div className="text-5xl">💎</div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-cyan-400 mb-3">Digital Tangibles</h3>
                  <p className="text-gray-300 mb-4">
                    Repackaging music as a valued asset. Each purchase includes interactive liner notes, 
                    digital lyric books, and behind-the-scenes content.
                  </p>
                  <ul className="text-gray-400 space-y-2">
                    <li>• Album artwork and liner notes</li>
                    <li>• Exclusive artist content</li>
                    <li>• Lyrics and credits</li>
                    <li>• Behind-the-scenes media</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Industry Reporting */}
            <div className="bg-gray-900/80 border border-gray-700 rounded-xl p-8">
              <div className="flex items-start space-x-6">
                <div className="text-5xl">📊</div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-cyan-400 mb-3">Industry Sales Reporting</h3>
                  <p className="text-gray-300 mb-4">
                    Full integration with music industry reporting systems. Your sales count toward Billboard charts 
                    and RIAA certifications (Gold, Platinum, Diamond).
                  </p>
                  <ul className="text-gray-400 space-y-2">
                    <li>• UPC/ISRC code support</li>
                    <li>• Luminate/SoundScan compatible</li>
                    <li>• Billboard chart eligibility</li>
                    <li>• RIAA certification tracking</li>
                    <li>• CSV export for manual reporting</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Artist Analytics */}
            <div className="bg-gray-900/80 border border-gray-700 rounded-xl p-8">
              <div className="flex items-start space-x-6">
                <div className="text-5xl">📈</div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-cyan-400 mb-3">Complete Analytics</h3>
                  <p className="text-gray-300 mb-4">
                    Artists get complete visibility into their business. Track sales, revenue, and fan engagement 
                    with powerful analytics tools.
                  </p>
                  <ul className="text-gray-400 space-y-2">
                    <li>• Real-time sales tracking</li>
                    <li>• Revenue breakdown (90/10 split visualization)</li>
                    <li>• Fan list with purchase history</li>
                    <li>• Sales by album reports</li>
                    <li>• Email access to buyers</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="max-w-5xl mx-auto mb-20">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">How It Works</h2>
          
          <div className="grid md:grid-cols-2 gap-12">
            {/* For Artists */}
            <div className="bg-gradient-to-br from-cyan-900/20 to-gray-900/80 border border-cyan-700 rounded-xl p-8">
              <h3 className="text-3xl font-bold text-cyan-400 mb-6">For Artists</h3>
              <ol className="space-y-4 text-gray-300">
                <li className="flex items-start space-x-3">
                  <span className="text-cyan-400 font-bold">1.</span>
                  <span><strong>Sign Up</strong> - First 100 accounts get FREE uploads forever! After that, $10 per album/single.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-cyan-400 font-bold">2.</span>
                  <span><strong>Upload Music</strong> - Add albums with UPC/ISRC codes, set your price, upload high-quality audio files.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-cyan-400 font-bold">3.</span>
                  <span><strong>Set Your Price</strong> - You control pricing. Keep 90% of every sale.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-cyan-400 font-bold">4.</span>
                  <span><strong>Track Sales</strong> - View analytics, fan data, and export reports for industry tracking.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-cyan-400 font-bold">5.</span>
                  <span><strong>Get Paid</strong> - Keep 90% of revenue. Build direct relationships with fans.</span>
                </li>
              </ol>
            </div>

            {/* For Fans */}
            <div className="bg-gradient-to-br from-green-900/20 to-gray-900/80 border border-green-700 rounded-xl p-8">
              <h3 className="text-3xl font-bold text-green-400 mb-6">For Fans</h3>
              <ol className="space-y-4 text-gray-300">
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 font-bold">1.</span>
                  <span><strong>Browse</strong> - Discover music directly from artists in the marketplace.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 font-bold">2.</span>
                  <span><strong>Purchase</strong> - Buy albums at artist-set prices. Support creators directly.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 font-bold">3.</span>
                  <span><strong>Own Forever</strong> - Music is yours to keep. No subscriptions, no expiration.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 font-bold">4.</span>
                  <span><strong>Stream Anywhere</strong> - Access your collection in The Vault from any device.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-green-400 font-bold">5.</span>
                  <span><strong>Enjoy Quality</strong> - High-fidelity audio with liner notes and exclusive content.</span>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="max-w-5xl mx-auto mb-20">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">Transparent Pricing</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-900/80 border border-gray-700 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">For Fans</h3>
              <div className="text-5xl font-bold text-green-400 mb-2">Free</div>
              <p className="text-gray-400 mb-6">Browse & Buy Music</p>
              <ul className="text-gray-300 text-left space-y-2">
                <li>✅ No subscription fees</li>
                <li>✅ Pay once, own forever</li>
                <li>✅ The Vault access included</li>
                <li>✅ High-fidelity streaming</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-cyan-900/30 to-cyan-800/20 border-2 border-cyan-500 rounded-xl p-8 text-center relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-cyan-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                  EARLY ADOPTER
                </span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4 mt-2">First 100 Artists</h3>
              <div className="text-5xl font-bold text-cyan-400 mb-2">$0</div>
              <p className="text-gray-400 mb-6">All Uploads FREE Forever</p>
              <ul className="text-gray-300 text-left space-y-2">
                <li>✅ Free uploads forever</li>
                <li>✅ Keep 90% of sales</li>
                <li>✅ Unlimited albums & singles</li>
                <li>✅ Full analytics access</li>
                <li>✅ Industry reporting tools</li>
              </ul>
            </div>

            <div className="bg-gray-900/80 border border-gray-700 rounded-xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Artists (101+)</h3>
              <div className="text-5xl font-bold text-white mb-2">$10</div>
              <p className="text-gray-400 mb-6">Per Album/Single Upload</p>
              <ul className="text-gray-300 text-left space-y-2">
                <li>✅ Pay per project uploaded</li>
                <li>✅ Keep 90% of all sales</li>
                <li>✅ Complete analytics</li>
                <li>✅ Fan data access</li>
                <li>✅ Industry reporting</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-green-900/20 border border-green-700 rounded-xl p-6 text-center">
            <p className="text-green-300 text-lg">
              <strong>Platform Fee:</strong> Only 10% per transaction. Artists keep 90% of every sale.
            </p>
          </div>
        </div>

        {/* Mission Statement */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl font-bold text-white mb-6">Our Mission</h2>
          <p className="text-2xl text-gray-300 leading-relaxed">
            "We're not just selling files; we're building a bridge between the creator and the super-fan. 
            Origin Audio isn't just a store—it's the new home for music ownership. 
            We are moving from the era of <span className="text-red-400">renting access</span> back to the era of <span className="text-cyan-400">owning culture</span>."
          </p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={() => navigate('/')}
            className="px-12 py-5 bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-500 hover:to-cyan-600 text-white text-2xl font-bold rounded-full transition transform hover:scale-105 shadow-2xl"
            data-testid="about-cta-button"
          >
            Join Origin Audio
          </button>
          <p className="text-gray-400 mt-4">Start your journey. Own your music.</p>
        </div>
      </div>
    </div>
  );
};


// Protected Route Component
const ProtectedRoute = ({ children, requireRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Handle array of roles or single role
  const allowedRoles = Array.isArray(requireRole) ? requireRole : [requireRole];
  if (requireRole && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Navigation Component
const Navigation = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="bg-black border-b border-gray-700">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/origin-logo.png" 
              alt="Origin Audio" 
              className="h-12 object-contain"
            />
            <span className="text-xs text-gray-400 italic">Music from the source</span>
          </Link>
          
          <div className="flex items-center space-x-6">
            {user ? (
              <>
                <Link to="/about" className="text-gray-300 hover:text-cyan-400 transition">
                  About Us
                </Link>
                <Link to="/marketplace" className="text-gray-300 hover:text-cyan-400 transition">
                  Marketplace
                </Link>
                <Link to="/ai-marketplace" className="text-gray-300 hover:text-purple-400 transition">
                  🤖 AI Music
                </Link>
                <Link to="/beats-marketplace" className="text-gray-300 hover:text-orange-400 transition">
                  🎹 Beats
                </Link>
                {user.role === 'super_fan' && (
                  <Link to="/vault" className="text-gray-300 hover:text-cyan-400 transition">
                    My Vault
                  </Link>
                )}
                {(user.role === 'artist' || user.role === 'label' || user.role === 'ai_creator' || user.role === 'beat_maker') && (
                  <>
                    <Link to="/artist/dashboard" className="text-gray-300 hover:text-cyan-400 transition">
                      Dashboard
                    </Link>
                    <Link to="/artist/upload" className="text-gray-300 hover:text-cyan-400 transition">
                      Upload Music
                    </Link>
                  </>
                )}
                <div className="flex items-center space-x-4">
                  <span className="text-gray-400">
                    {user.name} <span className="text-xs">
                      ({user.role === 'super_fan' ? 'Super Fan' : 
                        user.role === 'ai_creator' ? 'AI Creator' :
                        user.role === 'beat_maker' ? 'Beat Maker' :
                        user.role.charAt(0).toUpperCase() + user.role.slice(1)})
                    </span>
                  </span>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white rounded-lg transition border border-gray-600"
                    data-testid="logout-button"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link to="/marketplace" className="text-gray-300 hover:text-cyan-400 transition font-semibold">
                  Marketplace
                </Link>
                <Link to="/ai-marketplace" className="text-gray-300 hover:text-purple-400 transition font-semibold">
                  🤖 AI Music
                </Link>
                <Link to="/about" className="text-gray-300 hover:text-cyan-400 transition font-semibold">
                  About Us
                </Link>
                <Link to="/" className="px-6 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-bold rounded-lg transition">
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// Landing Page
const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);

  useEffect(() => {
    if (user) {
      if (user.role === 'artist' || user.role === 'label' || user.role === 'ai_creator' || user.role === 'beat_maker') {
        navigate('/artist/dashboard');
      } else {
        navigate('/marketplace');
      }
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800">
      <div className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <img 
            src="/origin-logo.png" 
            alt="Origin Audio" 
            className="h-32 mx-auto mb-8 object-contain"
            data-testid="hero-title"
          />
          <p className="text-3xl text-gray-300 mb-4 font-light">
            Music from the source
          </p>
          <p className="text-xl text-gray-400 mb-8">
            Stop streaming. Start owning.
          </p>
          
          <div className="max-w-3xl mx-auto bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">The Streaming Trap</h2>
            <p className="text-gray-300 text-lg mb-6">
              Artists need <span className="text-cyan-400 font-bold">3,000 streams</span> to buy a $15 pizza.
            </p>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-6">
                <h3 className="text-xl font-bold text-red-400 mb-2">Streaming</h3>
                <p className="text-red-300">$0.003 - $0.005 per stream</p>
                <p className="text-sm text-red-400 mt-2">Artists get pennies</p>
              </div>
              <div className="bg-green-900/30 border border-green-700 rounded-lg p-6">
                <h3 className="text-xl font-bold text-green-400 mb-2">Origin Audio</h3>
                <p className="text-green-300">90/10 Split</p>
                <p className="text-sm text-green-400 mt-2">90% goes to the artist</p>
              </div>
            </div>
          </div>

          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setShowAuth(true)}
              className="px-12 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white text-xl font-bold rounded-full transition transform hover:scale-105 shadow-2xl"
              data-testid="get-started-button"
            >
              Get Started
            </button>
            <Link
              to="/marketplace"
              className="px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white text-lg font-bold rounded-full transition transform hover:scale-105 shadow-2xl border border-gray-600"
            >
              🎵 Music
            </Link>
            <Link
              to="/ai-marketplace"
              className="px-8 py-4 bg-purple-800 hover:bg-purple-700 text-white text-lg font-bold rounded-full transition transform hover:scale-105 shadow-2xl border border-purple-600"
            >
              🤖 AI Music
            </Link>
            <Link
              to="/beats-marketplace"
              className="px-8 py-4 bg-orange-800 hover:bg-orange-700 text-white text-lg font-bold rounded-full transition transform hover:scale-105 shadow-2xl border border-orange-600"
            >
              🎹 Beats
            </Link>
            <Link
              to="/about"
              className="px-8 py-4 bg-transparent hover:bg-gray-800 text-white text-lg font-bold rounded-full transition border-2 border-cyan-500"
            >
              Learn More
            </Link>
          </div>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mt-20">
          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl p-6 text-center hover:border-cyan-500 transition">
            <div className="text-4xl mb-4">🎵</div>
            <h3 className="text-xl font-bold text-white mb-2">The Vault</h3>
            <p className="text-gray-400">High-fidelity lossless audio streaming. Your library, anywhere.</p>
          </div>
          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl p-6 text-center hover:border-cyan-500 transition">
            <div className="text-4xl mb-4">💎</div>
            <h3 className="text-xl font-bold text-white mb-2">Digital Tangibles</h3>
            <p className="text-gray-400">Liner notes, lyrics, and exclusive content with every purchase.</p>
          </div>
          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl p-6 text-center hover:border-cyan-500 transition">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-xl font-bold text-white mb-2">Data Sovereignty</h3>
            <p className="text-gray-400">Artists own their fan relationships and data.</p>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      {showAuth && <AuthModal isLogin={isLogin} setIsLogin={setIsLogin} onClose={() => setShowAuth(false)} />}
    </div>
  );
};

// Auth Modal Component
const AuthModal = ({ isLogin, setIsLogin, onClose }) => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'fan',
    bio: '',
    referral_code: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      login(response.data.access_token, response.data.user);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 max-w-md w-full mx-4 border border-gray-700" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-3xl font-bold text-white mb-6" data-testid="auth-modal-title">
          {isLogin ? 'Welcome Back' : 'Join Origin Audio'}
        </h2>

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <>
              <input
                type="text"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                required
                data-testid="register-name-input"
              />
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                data-testid="register-role-select"
              >
                <option value="">Select Your Role...</option>
                <option value="artist">🎤 Artist</option>
                <option value="label">🏢 Label</option>
                <option value="ai_creator">🤖 AI Creator</option>
                <option value="beat_maker">🎹 Beat Maker</option>
                <option value="super_fan">⭐ Super Fan</option>
              </select>
              {(formData.role === 'artist' || formData.role === 'label' || formData.role === 'ai_creator' || formData.role === 'beat_maker') && (
                <textarea
                  placeholder={
                    formData.role === 'artist' ? "Tell us about your music..." :
                    formData.role === 'label' ? "Tell us about your label..." :
                    formData.role === 'beat_maker' ? "Tell us about your beats..." :
                    "Tell us about your AI music creation..."
                  }
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 h-24"
                  data-testid="register-bio-input"
                />
              )}
              {(formData.role === 'artist' || formData.role === 'label' || formData.role === 'ai_creator' || formData.role === 'beat_maker') && (
                <div className="bg-cyan-900/20 border border-cyan-600 rounded-lg p-4">
                  <label className="block text-cyan-400 font-bold mb-2">Referral Code (Optional)</label>
                  <input
                    type="text"
                    placeholder="Enter creator referral code"
                    value={formData.referral_code}
                    onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 uppercase"
                    data-testid="register-referral-input"
                    maxLength="8"
                  />
                  <p className="text-xs text-cyan-300 mt-2">
                    💰 Referred by another creator? They'll earn 1% of your sales for life!
                  </p>
                </div>
              )}
            </>
          )}
          
          <input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400"
            required
            data-testid="auth-email-input"
          />
          
          <input
            type="password"
            placeholder="Password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400"
            required
            data-testid="auth-password-input"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full px-6 py-3 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-cyan-600 hover:to-cyan-700 text-white font-bold rounded-lg transition disabled:opacity-50 border border-gray-600"
            data-testid="auth-submit-button"
          >
            {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Create Account')}
          </button>
        </form>

        <button
          onClick={() => setIsLogin(!isLogin)}
          className="w-full mt-4 text-gray-400 hover:text-cyan-400 transition"
          data-testid="auth-toggle-button"
        >
          {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Login'}
        </button>
      </div>
    </div>
  );
};

// Marketplace Component
const Marketplace = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API}/albums`);
      setAlbums(response.data);
    } catch (error) {
      console.error('Failed to fetch albums:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-20 text-center text-white">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800">
      <Navigation />
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-5xl font-bold text-white mb-8" data-testid="marketplace-title">Marketplace</h1>
        <p className="text-gray-400 text-xl mb-12">Discover and own music directly from artists</p>

        {albums.length === 0 ? (
          <div className="text-center text-gray-400 text-xl py-20">
            No albums available yet. Check back soon!
          </div>
        ) : (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-8">
            {albums.map((album) => (
              <AlbumCard key={album.id} album={album} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Album Card Component
const AlbumCard = ({ album }) => {
  const navigate = useNavigate();
  const [playingPreview, setPlayingPreview] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null);

  const handlePreview = async (e) => {
    e.stopPropagation(); // Prevent card click

    // If already playing, pause
    if (playingPreview && previewAudio) {
      previewAudio.pause();
      setPlayingPreview(false);
      return;
    }

    // Get first track for preview
    if (!album.tracks || album.tracks.length === 0) {
      alert('No tracks available for preview');
      return;
    }

    const firstTrack = album.tracks[0];
    const audio = new Audio(`${BACKEND_URL}/api/preview/${firstTrack.id}`);
    
    audio.onended = () => {
      setPlayingPreview(false);
      setPreviewAudio(null);
    };

    audio.onerror = () => {
      setPlayingPreview(false);
      alert('Preview not available');
    };

    try {
      setPreviewAudio(audio);
      setPlayingPreview(true);
      await audio.play();
    } catch (error) {
      console.error('Preview error:', error);
      setPlayingPreview(false);
    }
  };

  return (
    <div
      className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl overflow-hidden hover:transform hover:scale-105 hover:border-cyan-500 transition cursor-pointer"
      data-testid={`album-card-${album.id}`}
    >
      {/* Artist Info Section */}
      <div className="p-4 bg-gray-800/50 border-b border-gray-700">
        <div className="flex items-center space-x-3">
          {/* Artist Profile Picture */}
          <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-cyan-500 bg-gray-700 flex items-center justify-center flex-shrink-0">
            {album.artist_profile_image ? (
              <img 
                src={`${BACKEND_URL}${album.artist_profile_image}`} 
                alt={album.artist_name} 
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-xl">🎤</span>
            )}
          </div>
          {/* Artist Name */}
          <div className="flex-1 min-w-0">
            <p className="text-cyan-400 font-bold text-sm truncate" data-testid="album-artist">
              {album.artist_name}
            </p>
            <p className="text-gray-500 text-xs">
              {album.release_type === 'mixtape' ? 'Mixtape' : 'Album'}
            </p>
          </div>
        </div>
      </div>

      {/* Album Cover - Clickable */}
      <div 
        onClick={() => navigate(`/album/${album.id}`)}
        className="aspect-square bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center relative"
      >
        {album.cover_url ? (
          <img src={`${BACKEND_URL}${album.cover_url}`} alt={album.title} className="w-full h-full object-cover" />
        ) : (
          <span className="text-6xl">🎵</span>
        )}
        {album.release_type === 'mixtape' && (
          <div className="absolute top-2 right-2 bg-cyan-500 text-black px-3 py-1 rounded-full text-xs font-bold">
            MIXTAPE
          </div>
        )}
        {album.is_presale && (
          <div className="absolute top-2 left-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-3 py-1 rounded-full text-xs font-bold shadow-lg">
            PRE-SALE
          </div>
        )}
        
        {/* Preview Button Overlay */}
        {album.tracks && album.tracks.length > 0 && (
          <div className="absolute inset-0 bg-black/0 hover:bg-black/50 transition flex items-center justify-center opacity-0 hover:opacity-100">
            <button
              onClick={handlePreview}
              className={`px-6 py-3 rounded-full font-bold transition shadow-lg ${
                playingPreview 
                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                  : 'bg-cyan-600 hover:bg-cyan-700 text-white'
              }`}
            >
              {playingPreview ? '⏸ Stop Preview' : '▶ Preview'}
            </button>
          </div>
        )}
      </div>

      {/* Album Info */}
      <div className="p-4" onClick={() => navigate(`/album/${album.id}`)}>
        <h3 className="text-lg font-bold text-white mb-2 truncate" data-testid="album-title">
          {album.title}
        </h3>
        {album.is_presale && album.presale_release_date && (
          <p className="text-yellow-400 text-xs mb-2">
            Coming {new Date(album.presale_release_date).toLocaleDateString()}
          </p>
        )}
        <p className="text-green-400 font-bold text-xl" data-testid="album-price">
          ${album.price.toFixed(2)}
        </p>
      </div>
    </div>
  );
};

// AI Marketplace Component
const AIMarketplace = () => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAIAlbums();
  }, []);

  const fetchAIAlbums = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-albums`);
      const data = await response.json();
      setAlbums(data);
    } catch (err) {
      setError('Failed to load AI music');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-indigo-900">
      <Navigation />
      <div className="container mx-auto px-4 py-20">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 mb-4">
            🤖 AI Music Marketplace
          </h1>
          <p className="text-xl text-purple-300 mb-6">
            Discover AI-generated music from the future
          </p>
          <div className="bg-purple-900/30 border border-purple-500 rounded-xl p-6 max-w-2xl mx-auto">
            <p className="text-purple-200">
              <strong className="text-purple-400">80/20 Split:</strong> AI creators earn 80% of each sale
            </p>
            <p className="text-purple-300 text-sm mt-2">
              All music in this marketplace is AI-generated and clearly labeled
            </p>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        )}

        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-200 px-6 py-4 rounded-lg max-w-2xl mx-auto">
            {error}
          </div>
        )}

        {!loading && albums.length === 0 && (
          <div className="text-center text-purple-300 text-xl">
            No AI music available yet. Be the first AI creator to upload!
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {albums.map((album) => (
            <AIAlbumCard key={album.id} album={album} />
          ))}
        </div>
      </div>
    </div>
  );
};

// AI Album Card Component
const AIAlbumCard = ({ album }) => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-gradient-to-br from-purple-900/80 to-indigo-900/80 backdrop-blur-sm border-2 border-purple-500 rounded-xl overflow-hidden hover:border-pink-500 transition cursor-pointer shadow-lg shadow-purple-500/20">
      <div 
        onClick={() => navigate(`/album/${album.id}`)}
        className="aspect-square bg-gradient-to-br from-purple-700 to-indigo-900 flex items-center justify-center relative"
      >
        {album.cover_url ? (
          <img src={`${BACKEND_URL}${album.cover_url}`} alt={album.title} className="w-full h-full object-cover" />
        ) : (
          <span className="text-6xl">🤖</span>
        )}
        
        {/* AI Badge */}
        <div className="absolute top-2 left-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
          🤖 AI-GENERATED
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-lg font-bold text-purple-200 mb-2 truncate">
          {album.title}
        </h3>
        <p className="text-purple-400 text-sm mb-3">{album.artist_name}</p>
        <p className="text-green-400 font-bold text-xl">
          ${album.price.toFixed(2)}
        </p>
        <p className="text-purple-300 text-xs mt-2">
          80% to creator • 20% platform
        </p>
      </div>
    </div>
  );
};

// Beats Marketplace Component
const BeatsMarketplace = () => {
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBeats();
  }, []);

  const fetchBeats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/beats`);
      const data = await response.json();
      setBeats(data);
    } catch (err) {
      setError('Failed to load beats');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-900 via-black to-red-900">
      <Navigation />
      <div className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <h1 className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-400 to-yellow-400 mb-4">
            🎹 Beats Marketplace
          </h1>
          <p className="text-xl text-orange-300 mb-6">
            Premium beats from talented producers
          </p>
          <div className="bg-orange-900/30 border border-orange-500 rounded-xl p-6 max-w-2xl mx-auto">
            <p className="text-orange-200">
              <strong className="text-orange-400">90/10 Split:</strong> Beat makers earn 90% of each sale
            </p>
            <p className="text-orange-300 text-sm mt-2">
              Non-Exclusive (unlimited sales) or Exclusive (one-time sale) licenses
            </p>
          </div>
        </div>

        {loading && <div className="flex justify-center"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div></div>}
        {error && <div className="bg-red-500/20 border border-red-500 text-red-200 px-6 py-4 rounded-lg max-w-2xl mx-auto">{error}</div>}
        {!loading && beats.length === 0 && <div className="text-center text-orange-300 text-xl">No beats available yet. Be the first beat maker to upload!</div>}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {beats.map((beat) => <BeatCard key={beat.id} beat={beat} />)}
        </div>
      </div>
    </div>
  );
};

// Beat Card Component  
const BeatCard = ({ beat }) => {
  const navigate = useNavigate();
  const [playingPreview, setPlayingPreview] = useState(false);
  const [audio, setAudio] = useState(null);

  const handlePreview = async (e) => {
    e.stopPropagation();
    if (playingPreview) { audio?.pause(); setPlayingPreview(false); return; }
    const newAudio = new Audio(`${BACKEND_URL}/api/beats/${beat.id}/preview`);
    newAudio.onended = () => setPlayingPreview(false);
    try { await newAudio.play(); setAudio(newAudio); setPlayingPreview(true); } 
    catch (error) { console.error('Preview error:', error); }
  };

  return (
    <div className="bg-gradient-to-br from-orange-900/80 to-red-900/80 backdrop-blur-sm border-2 border-orange-500 rounded-xl overflow-hidden hover:border-yellow-500 transition cursor-pointer shadow-lg shadow-orange-500/20">
      <div onClick={() => navigate(`/beat/${beat.id}`)} className="aspect-square bg-gradient-to-br from-orange-700 to-red-900 flex items-center justify-center relative">
        {beat.cover_url ? <img src={`${BACKEND_URL}${beat.cover_url}`} alt={beat.title} className="w-full h-full object-cover" /> : <span className="text-6xl">🎹</span>}
        <div className="absolute top-2 left-2 bg-gradient-to-r from-orange-600 to-red-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">🎹 BEAT</div>
        {beat.is_exclusive_sold && <div className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold">SOLD</div>}
        <div className="absolute inset-0 bg-black/0 hover:bg-black/50 transition flex items-center justify-center opacity-0 hover:opacity-100">
          <button onClick={handlePreview} className={`px-6 py-3 rounded-full font-bold transition shadow-lg ${playingPreview ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-orange-600 hover:bg-orange-700 text-white'}`}>
            {playingPreview ? '⏸ Stop' : '▶ Preview'}
          </button>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-lg font-bold text-orange-200 mb-1 truncate">{beat.title}</h3>
        <p className="text-orange-400 text-sm mb-2">{beat.beat_maker_name}</p>
        <div className="flex items-center gap-2 mb-3 text-xs">
          <span className="bg-orange-700 text-orange-200 px-2 py-1 rounded">{beat.genre}</span>
          {beat.bpm && <span className="text-orange-300">{beat.bpm} BPM</span>}
          {beat.key && <span className="text-orange-300">{beat.key}</span>}
        </div>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-orange-300 text-sm">Non-Exclusive</span>
            <span className="text-green-400 font-bold">${beat.non_exclusive_price.toFixed(2)}</span>
          </div>
          {!beat.is_exclusive_sold && (
            <div className="flex justify-between items-center">
              <span className="text-orange-300 text-sm">Exclusive</span>
              <span className="text-yellow-400 font-bold">${beat.exclusive_price.toFixed(2)}</span>
            </div>
          )}
        </div>
        {beat.times_sold_non_exclusive > 0 && <p className="text-orange-400 text-xs mt-2">{beat.times_sold_non_exclusive} non-exclusive sales</p>}
      </div>
    </div>
  );
};

// Album Detail Page
const AlbumDetail = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');
  const [playingPreview, setPlayingPreview] = useState(null);  // Track ID currently playing
  const [previewAudio, setPreviewAudio] = useState(null);  // Audio element
  const [showGuestCheckout, setShowGuestCheckout] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');

  useEffect(() => {
    fetchAlbum();
  }, [id]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (previewAudio) {
        previewAudio.pause();
        previewAudio.src = '';
      }
    };
  }, [previewAudio]);

  const fetchAlbum = async () => {
    try {
      const response = await axios.get(`${API}/albums/${id}`);
      setAlbum(response.data);
    } catch (error) {
      console.error('Failed to fetch album:', error);
      setError('Album not found');
    } finally {
      setLoading(false);
    }
  };

  const togglePreview = async (trackId) => {
    try {
      // If clicking the same track, toggle play/pause
      if (playingPreview === trackId && previewAudio) {
        if (previewAudio.paused) {
          await previewAudio.play();
        } else {
          previewAudio.pause();
          setPlayingPreview(null);
        }
        return;
      }

      // Stop current preview if playing
      if (previewAudio) {
        previewAudio.pause();
        previewAudio.src = '';
        setPlayingPreview(null);
      }

      // Create new audio element for the preview
      const audio = new Audio(`${API}/preview/${trackId}`);
      
      // When preview ends, reset state
      audio.onended = () => {
        setPlayingPreview(null);
        setPreviewAudio(null);
      };

      // Handle errors
      audio.onerror = (e) => {
        console.error('Preview error:', e);
        setPlayingPreview(null);
        setPreviewAudio(null);
        alert('Failed to load preview. Please try again.');
      };

      // Set state before playing to ensure UI updates
      setPreviewAudio(audio);
      setPlayingPreview(trackId);

      // Attempt to play with proper promise handling
      await audio.play();
      
    } catch (error) {
      console.error('Error playing preview:', error);
      // Reset state if play fails
      setPlayingPreview(null);
      setPreviewAudio(null);
      
      // Provide user-friendly error message
      if (error.name === 'NotAllowedError') {
        alert('Browser blocked audio playback. Please try clicking the preview button again.');
      } else {
        alert('Unable to play preview. Please try again.');
      }
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      // Show guest checkout modal instead of requiring login
      setShowGuestCheckout(true);
      return;
    }

    setPurchasing(true);
    setError('');

    try {
      // Create Stripe checkout session
      const origin = window.location.origin;
      const response = await axios.post(
        `${API}/payments/create-checkout`,
        {
          package_id: album.id,  // Album ID
          origin_url: origin,
          metadata: {
            album_title: album.title,
            artist_name: album.artist_name
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Purchase failed');
      setPurchasing(false);
    }
  };

  const handleGuestPurchase = async (e) => {
    e.preventDefault();
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setPurchasing(true);
    setError('');

    try {
      // Create Stripe checkout session for guest
      const origin = window.location.origin;
      const response = await axios.post(
        `${API}/payments/create-checkout`,
        {
          package_id: album.id,
          origin_url: origin,
          guest_email: guestEmail,
          metadata: {
            album_title: album.title,
            artist_name: album.artist_name
          }
        }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Purchase failed');
      setPurchasing(false);
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-20 text-center text-white">Loading...</div>;
  }

  if (error || !album) {
    return <div className="container mx-auto px-4 py-20 text-center text-red-400">{error || 'Album not found'}</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800">
      <Navigation />
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="aspect-square bg-gradient-to-br from-gray-700 to-gray-900 rounded-2xl flex items-center justify-center mb-6 border border-gray-700">
                {album.cover_url ? (
                  <img src={`${BACKEND_URL}${album.cover_url}`} alt={album.title} className="w-full h-full object-cover rounded-2xl" />
                ) : (
                  <span className="text-9xl">🎵</span>
                )}
              </div>
            </div>

            <div className="text-white">
              <div className="flex items-center space-x-4 mb-4">
                {/* Artist Profile Picture */}
                {album.artist_profile_image && (
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-cyan-500 bg-gray-800">
                    <img src={`${BACKEND_URL}${album.artist_profile_image}`} alt={album.artist_name} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h1 className="text-5xl font-bold" data-testid="album-detail-title">{album.title}</h1>
                    {album.release_type === 'mixtape' && (
                      <span className="bg-cyan-500 text-black px-4 py-2 rounded-full text-sm font-bold">
                        🎤 MIXTAPE
                      </span>
                    )}
                    {album.is_presale && (
                      <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                        🎯 PRE-SALE
                      </span>
                    )}
                  </div>
                  <p className="text-2xl text-gray-400 mt-2" data-testid="album-detail-artist">{album.artist_name}</p>
                  {album.artist_bio && (
                    <p className="text-gray-400 text-sm mt-2 italic">"{album.artist_bio}"</p>
                  )}
                </div>
              </div>
              <p className="text-gray-300 mb-8">{album.description}</p>

              {album.is_presale && album.presale_release_date && (
                <div className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-600 rounded-lg p-6 mb-6">
                  <div className="flex items-start space-x-4">
                    <span className="text-4xl">🎯</span>
                    <div>
                      <h3 className="text-yellow-400 font-bold text-lg mb-2">Pre-Sale Album - Coming Soon!</h3>
                      <p className="text-yellow-200 mb-2">
                        <strong>Release Date:</strong> {new Date(album.presale_release_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                      <ul className="text-gray-300 text-sm space-y-1">
                        <li>✓ Purchase now and preview all tracks (30 seconds each)</li>
                        <li>✓ Full streaming & download unlocks on release date</li>
                        <li>✓ Your purchase supports the artist immediately</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {album.release_type === 'mixtape' && (
                <div className="bg-cyan-900/20 border border-cyan-600 rounded-lg p-4 mb-6">
                  <p className="text-cyan-300 text-sm">
                    <strong>Direct-to-Fan Release:</strong> This mixtape is a personal release from the artist. 
                    No industry codes, just pure music between artist and fans.
                  </p>
                </div>
              )}

              <div className="bg-gray-900/80 border border-gray-700 rounded-xl p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-2xl font-bold text-green-400" data-testid="album-detail-price">${album.price.toFixed(2)}</span>
                  <button
                    onClick={handlePurchase}
                    disabled={purchasing}
                    className="px-8 py-3 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-cyan-600 hover:to-cyan-700 text-white font-bold rounded-lg transition disabled:opacity-50 border border-gray-600"
                    data-testid="purchase-button"
                  >
                    {purchasing ? 'Processing...' : 'Purchase Album'}
                  </button>
                </div>
                <p className="text-sm text-gray-400">90% goes directly to the artist</p>
              </div>

              {error && (
                <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6">
                  {error}
                </div>
              )}

              <div>
                <h2 className="text-2xl font-bold mb-4">Track List</h2>
                <div className="space-y-2">
                  {album.tracks && album.tracks.map((track) => (
                    <div key={track.id} className="flex justify-between items-center bg-gray-900/60 border border-gray-700 rounded-lg p-4 hover:border-cyan-500 transition">
                      <div className="flex items-center space-x-4 flex-1">
                        <span className="text-gray-500 font-mono">{track.track_number}</span>
                        <span data-testid={`track-title-${track.track_number}`}>{track.title}</span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePreview(track.id);
                          }}
                          className={`px-4 py-2 rounded-lg font-bold transition ${
                            playingPreview === track.id
                              ? 'bg-cyan-600 text-white hover:bg-cyan-700'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                          data-testid={`preview-button-${track.track_number}`}
                        >
                          {playingPreview === track.id ? '⏸ Pause' : '▶ Preview'}
                        </button>
                        
                        {/* Single Purchase Button */}
                        {track.available_as_single && track.single_price && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!user) {
                                alert('Please login to purchase');
                                return;
                              }
                              try {
                                const origin = window.location.origin;
                                const response = await axios.post(
                                  `${API}/payments/create-checkout`,
                                  {
                                    package_id: `track_${track.id}`,
                                    origin_url: origin,
                                    metadata: {
                                      track_title: track.title,
                                      album_title: album.title
                                    }
                                  },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                window.location.href = response.data.url;
                              } catch (error) {
                                console.error('Purchase failed:', error);
                                alert(error.response?.data?.detail || 'Failed to purchase');
                              }
                            }}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition"
                          >
                            Buy ${track.single_price.toFixed(2)}
                          </button>
                        )}
                        
                        <span className="text-cyan-400 text-sm uppercase">{track.file_type}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Guest Checkout Modal */}
      {showGuestCheckout && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-8 max-w-md w-full border border-gray-700">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Guest Checkout</h2>
              <p className="text-gray-400 text-sm">No account needed! Enter your email to purchase</p>
            </div>

            <form onSubmit={handleGuestPurchase} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Download link will be sent to this email
                </p>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4 mb-4">
                <p className="text-yellow-200 text-xs">
                  <strong>📜 Copyright Notice:</strong> By purchasing, you agree that this music is for personal use only. 
                  Redistribution, resale, or commercial use is strictly prohibited.
                </p>
              </div>

              {error && (
                <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowGuestCheckout(false);
                    setGuestEmail('');
                    setError('');
                  }}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={purchasing}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-500 hover:to-cyan-600 text-white font-bold rounded-lg transition disabled:opacity-50"
                >
                  {purchasing ? 'Processing...' : 'Continue to Payment'}
                </button>
              </div>

              <div className="text-center pt-4 border-t border-gray-700">
                <p className="text-sm text-gray-400 mb-2">Already have an account?</p>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="text-cyan-400 hover:text-cyan-300 text-sm font-medium"
                >
                  Log in instead →
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// The Vault (User Library)
const Vault = () => {
  const { token } = useAuth();
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [currentAlbum, setCurrentAlbum] = useState(null);

  useEffect(() => {
    fetchLibrary();
  }, []);

  const fetchLibrary = async () => {
    try {
      const response = await axios.get(`${API}/library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLibrary(response.data);
    } catch (error) {
      console.error('Failed to fetch library:', error);
    } finally {
      setLoading(false);
    }
  };

  const playTrack = (track, album) => {
    setCurrentTrack(track);
    setCurrentAlbum(album);
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-20 text-center text-white">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800">
      <Navigation />
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-5xl font-bold text-white mb-4" data-testid="vault-title">The Vault</h1>
        <p className="text-gray-400 text-xl mb-12">Your personal music collection</p>

        {library.length === 0 ? (
          <div className="text-center text-gray-400 text-xl py-20">
            Your vault is empty. Visit the <Link to="/marketplace" className="text-cyan-400 underline">marketplace</Link> to start building your collection.
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {library.map((album) => (
                <div key={album.id} className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl overflow-hidden hover:border-cyan-500 transition" data-testid={`vault-album-${album.id}`}>
                  <div className="aspect-square bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center relative">
                    {album.cover_url ? (
                      <img src={`${BACKEND_URL}${album.cover_url}`} alt={album.title} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-6xl">🎵</span>
                    )}
                    {/* Pre-Sale Locked Overlay */}
                    {album.is_presale && !album.is_released && (
                      <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center">
                        <span className="text-6xl mb-3">🔒</span>
                        <p className="text-yellow-400 font-bold text-lg">LOCKED</p>
                        <p className="text-gray-300 text-sm px-4 text-center mt-2">
                          Releases {new Date(album.presale_release_date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="text-xl font-bold text-white">{album.title}</h3>
                      {album.is_presale && !album.is_released && (
                        <span className="bg-yellow-500 text-black px-2 py-1 rounded text-xs font-bold ml-2 whitespace-nowrap">
                          PRE-SALE
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm mb-4">{album.artist_name}</p>
                    
                    {album.is_presale && !album.is_released ? (
                      <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-4 text-center">
                        <p className="text-yellow-300 text-sm font-semibold mb-2">
                          🎯 Coming Soon
                        </p>
                        <p className="text-gray-300 text-xs">
                          Full access unlocks on<br />
                          <strong className="text-yellow-400">
                            {new Date(album.presale_release_date).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                          </strong>
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {album.tracks && album.tracks.map((track) => (
                          <button
                            key={track.id}
                            onClick={() => playTrack(track, album)}
                            className="w-full flex justify-between items-center bg-gray-800/50 hover:bg-gray-700/70 rounded-lg p-3 transition text-left border border-gray-700 hover:border-cyan-500"
                            data-testid={`play-track-${track.id}`}
                          >
                            <span className="text-white text-sm">
                              {track.track_number}. {track.title}
                            </span>
                            <span className="text-cyan-400">▶</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {currentTrack && (
              <AudioPlayer track={currentTrack} album={currentAlbum} token={token} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Audio Player Component
const AudioPlayer = ({ track, album, token }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const audioRef = useState(new Audio())[0];

  useEffect(() => {
    audioRef.src = `${API}/stream/${track.id}`;
    audioRef.headers = { Authorization: `Bearer ${token}` };
    
    // For authenticated requests with Audio API, we need to use fetch
    fetch(`${API}/stream/${track.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(response => response.blob())
      .then(blob => {
        audioRef.src = URL.createObjectURL(blob);
        audioRef.load();
      });

    audioRef.addEventListener('loadedmetadata', () => {
      setDuration(audioRef.duration);
    });

    audioRef.addEventListener('timeupdate', () => {
      setCurrentTime(audioRef.currentTime);
    });

    audioRef.addEventListener('ended', () => {
      setIsPlaying(false);
    });

    return () => {
      audioRef.pause();
    };
  }, [track.id]);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.pause();
    } else {
      audioRef.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    audioRef.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    audioRef.volume = newVolume;
    setVolume(newVolume);
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-black via-gray-900 to-black border-t border-gray-700 p-6 shadow-2xl" data-testid="audio-player">
      <div className="container mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center border border-gray-600">
              {album.cover_url ? (
                <img src={`${BACKEND_URL}${album.cover_url}`} alt={album.title} className="w-full h-full object-cover rounded-lg" />
              ) : (
                <span className="text-2xl">🎵</span>
              )}
            </div>
            <div>
              <h3 className="text-white font-bold" data-testid="now-playing-track">{track.title}</h3>
              <p className="text-gray-400 text-sm" data-testid="now-playing-album">{album.title} - {album.artist_name}</p>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            <button
              onClick={togglePlay}
              className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex items-center justify-center hover:from-cyan-400 hover:to-cyan-500 transition shadow-lg"
              data-testid="play-pause-button"
            >
              {isPlaying ? (
                <span className="text-2xl text-white">⏸</span>
              ) : (
                <span className="text-2xl text-white">▶</span>
              )}
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-gray-400">🔊</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              className="w-24"
              data-testid="volume-control"
            />
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <span className="text-gray-400 text-sm" data-testid="current-time">{formatTime(currentTime)}</span>
          <input
            type="range"
            min="0"
            max={duration || 0}
            step="0.1"
            value={currentTime}
            onChange={handleSeek}
            className="flex-1"
            data-testid="seek-bar"
          />
          <span className="text-gray-400 text-sm" data-testid="duration">{formatTime(duration)}</span>
        </div>

        <div className="mt-2 text-center text-sm text-cyan-400">
          High-Fidelity {track.file_type.toUpperCase()} • Lossless Quality
        </div>
      </div>
    </div>
  );
};

// Beat Upload Component
const BeatUpload = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [beatData, setBeatData] = useState({
    title: '',
    description: '',
    genre: '',
    bpm: '',
    key: '',
    non_exclusive_price: '',
    exclusive_price: ''
  });
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [genres, setGenres] = useState([]);
  const [suggestedPricing, setSuggestedPricing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchGenres();
    fetchPricingSuggestions();
  }, []);

  const fetchGenres = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/beats/genres`);
      const data = await response.json();
      setGenres(data.genres);
    } catch (err) {
      console.error('Error fetching genres:', err);
    }
  };

  const fetchPricingSuggestions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/beats/pricing-suggestions`);
      const data = await response.json();
      setSuggestedPricing(data);
    } catch (err) {
      console.error('Error fetching pricing:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!audioFile) {
      setError('Please upload an audio file');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('title', beatData.title);
      formData.append('description', beatData.description);
      formData.append('genre', beatData.genre);
      if (beatData.bpm) formData.append('bpm', beatData.bpm);
      if (beatData.key) formData.append('key', beatData.key);
      formData.append('non_exclusive_price', parseFloat(beatData.non_exclusive_price));
      formData.append('exclusive_price', parseFloat(beatData.exclusive_price));
      formData.append('audio_file', audioFile);
      if (coverFile) formData.append('cover', coverFile);

      const response = await axios.post(`${API}/beats/create`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('Beat uploaded successfully!');
      navigate('/artist/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload beat');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-900 via-black to-red-900 py-12">
      <Navigation />
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400 mb-8">Upload Beat</h1>

        {error && <div className="bg-red-500/20 border border-red-500 text-red-200 px-6 py-4 rounded-lg mb-6">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-orange-900/20 border border-orange-600 rounded-xl p-8 space-y-6">
            <div>
              <label className="block text-orange-300 mb-2 font-bold">Beat Title *</label>
              <input type="text" required value={beatData.title} onChange={(e) => setBeatData({...beatData, title: e.target.value})} className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-400" placeholder="e.g., Dark Trap Beat" />
            </div>

            <div>
              <label className="block text-orange-300 mb-2 font-bold">Description</label>
              <textarea value={beatData.description} onChange={(e) => setBeatData({...beatData, description: e.target.value})} className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-400 h-24" placeholder="Describe your beat..." />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-orange-300 mb-2 font-bold">Genre *</label>
                <select required value={beatData.genre} onChange={(e) => setBeatData({...beatData, genre: e.target.value})} className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-400">
                  <option value="">Select Genre</option>
                  {genres.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-orange-300 mb-2 font-bold">BPM</label>
                <input type="number" value={beatData.bpm} onChange={(e) => setBeatData({...beatData, bpm: e.target.value})} className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-400" placeholder="140" />
              </div>
              <div>
                <label className="block text-orange-300 mb-2 font-bold">Key</label>
                <input type="text" value={beatData.key} onChange={(e) => setBeatData({...beatData, key: e.target.value})} className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-400" placeholder="C Minor" />
              </div>
            </div>

            <div>
              <label className="block text-orange-300 mb-2 font-bold">Audio File *</label>
              <input type="file" required accept="audio/*" onChange={(e) => setAudioFile(e.target.files[0])} className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>

            <div>
              <label className="block text-orange-300 mb-2 font-bold">Cover Image</label>
              <input type="file" accept="image/*" onChange={(e) => setCoverFile(e.target.files[0])} className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          </div>

          {/* Pricing Section */}
          <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-500 rounded-xl p-8 space-y-6">
            <h2 className="text-2xl font-bold text-orange-400">💰 Pricing</h2>
            
            {suggestedPricing && (
              <div className="bg-orange-800/30 border border-orange-600 rounded-lg p-4">
                <p className="text-orange-300 font-bold mb-2">Suggested Pricing:</p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><p className="text-orange-200">Basic</p><p className="text-green-400">${suggestedPricing.non_exclusive.basic} / ${suggestedPricing.exclusive.basic}</p></div>
                  <div><p className="text-orange-200">Standard</p><p className="text-green-400">${suggestedPricing.non_exclusive.standard} / ${suggestedPricing.exclusive.standard}</p></div>
                  <div><p className="text-orange-200">Premium</p><p className="text-green-400">${suggestedPricing.non_exclusive.premium} / ${suggestedPricing.exclusive.premium}</p></div>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-orange-300 mb-2 font-bold">Non-Exclusive Price * <span className="text-sm font-normal">(Can sell unlimited times)</span></label>
                <input type="number" step="0.01" required value={beatData.non_exclusive_price} onChange={(e) => setBeatData({...beatData, non_exclusive_price: e.target.value})} className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-400" placeholder="29.99" />
              </div>
              <div>
                <label className="block text-orange-300 mb-2 font-bold">Exclusive Price * <span className="text-sm font-normal">(One-time sale only)</span></label>
                <input type="number" step="0.01" required value={beatData.exclusive_price} onChange={(e) => setBeatData({...beatData, exclusive_price: e.target.value})} className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-400" placeholder="299.99" />
              </div>
            </div>

            <div className="bg-gray-900 border border-orange-700 rounded p-4">
              <p className="text-orange-300 text-sm">• You earn <strong className="text-orange-400">90%</strong> of each sale • Upload fee: <strong className="text-orange-400">$5</strong> per beat</p>
            </div>
          </div>

          <button type="submit" disabled={uploading} className={`w-full py-4 rounded-lg font-bold text-lg transition ${uploading ? 'bg-gray-600 cursor-not-allowed' : 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white'}`}>
            {uploading ? 'Uploading...' : '🎹 Upload Beat'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Artist Upload Page
const ArtistUpload = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // State for regular album upload
  const [step, setStep] = useState(1); // 1: Album Info, 2: Tracks, 3: Singles Pricing
  const [albumData, setAlbumData] = useState({
    title: '',
    description: '',
    price: '',
    release_type: 'album',  // 'album' or 'mixtape'
    upc_code: '',
    label_name: '',
    territory: 'US',
    is_presale: false,  // Pre-sale toggle
    presale_release_date: ''  // Release date for pre-sale
  });
  const [coverFile, setCoverFile] = useState(null);
  const [albumId, setAlbumId] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [singlesSettings, setSinglesSettings] = useState({});  // Track ID => {enabled, price}
  const [termsAccepted, setTermsAccepted] = useState(false);  // Legal disclaimer acceptance
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [feeStatus, setFeeStatus] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    checkUploadFee();
  }, []);

  const checkUploadFee = async () => {
    try {
      const response = await axios.get(`${API}/payment/upload-fee/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeeStatus(response.data);
      
      // If fee is required and not paid, show payment modal
      if (response.data.required && !response.data.paid) {
        setShowPaymentModal(true);
      }
    } catch (error) {
      console.error('Failed to check upload fee:', error);
    }
  };

  const handlePayUploadFee = async () => {
    setProcessingPayment(true);
    setError('');

    try {
      // Create Stripe checkout session
      const origin = window.location.origin;
      const response = await axios.post(
        `${API}/payments/create-checkout`,
        {
          package_id: "upload_fee",
          origin_url: origin,
          metadata: {
            payment_type: "upload_fee",
            album_id: albumId
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed');
      setProcessingPayment(false);
    }
  };

  const handleAlbumSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('title', albumData.title);
      formData.append('description', albumData.description);
      formData.append('price', parseFloat(albumData.price));
      formData.append('release_type', albumData.release_type);
      formData.append('upc_code', albumData.upc_code);
      formData.append('label_name', albumData.label_name);
      formData.append('territory', albumData.territory);
      formData.append('is_presale', albumData.is_presale);
      if (albumData.is_presale && albumData.presale_release_date) {
        formData.append('presale_release_date', albumData.presale_release_date);
      }
      if (coverFile) {
        formData.append('cover', coverFile);
      }

      const response = await axios.post(`${API}/albums`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setAlbumId(response.data.id);
      
      // Check if payment is required for this album
      if (response.data.upload_fee_paid || response.data.upload_fee_waived) {
        // Album ready, go to track upload
        setStep(2);
      } else {
        // Show payment modal for this specific album
        setShowPaymentModal(true);
      }
    } catch (err) {
      if (err.response?.status === 402) {
        // Payment required - album was created but needs payment
        // Extract album ID from error if available
        setError(err.response?.data?.detail || 'Upload fee required for this album');
        // Album should be in response even with 402 error
        // We need to handle payment for this specific album
      } else {
        setError(err.response?.data?.detail || 'Failed to create album');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleTrackUpload = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');

    try {
      const trackFile = e.target.elements.audioFile.files[0];
      
      if (!trackFile) {
        setError('Please select an audio file');
        setUploading(false);
        return;
      }
      
      // Check file size (150MB limit - increased for high-quality masters)
      const fileSizeMB = trackFile.size / (1024 * 1024);
      if (fileSizeMB > 150) {
        setError(`File too large (${fileSizeMB.toFixed(1)}MB). Maximum size is 150MB`);
        setUploading(false);
        return;
      }
      
      // Check file type
      const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/flac', 'audio/x-m4a', 'audio/aac'];
      if (!allowedTypes.includes(trackFile.type) && !trackFile.name.match(/\.(mp3|wav|flac|m4a|aac)$/i)) {
        setError('Only MP3, WAV, FLAC, M4A, and AAC files are supported');
        setUploading(false);
        return;
      }
      
      const trackTitle = e.target.elements.trackTitle.value;
      const isrcCode = e.target.elements.isrcCode.value;
      const trackNumber = tracks.length + 1;

      const formData = new FormData();
      formData.append('title', trackTitle);
      formData.append('track_number', trackNumber);
      formData.append('isrc_code', isrcCode);
      formData.append('audio_file', trackFile);

      const response = await axios.post(`${API}/albums/${albumId}/tracks`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000, // 2 minute timeout for large files
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });

      setTracks([...tracks, response.data]);
      e.target.reset();
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Upload error:', err);
      if (err.code === 'ECONNABORTED') {
        setError('Upload timed out. Please try a smaller file or check your connection.');
      } else if (err.response?.status === 413) {
        setError('File too large. Maximum size is 100MB.');
      } else {
        setError(err.response?.data?.detail || 'Failed to upload track. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const finishUpload = () => {
    navigate('/artist/dashboard');
  };

  // If user is beat maker, redirect to beat upload
  if (user && user.role === 'beat_maker') {
    return <BeatUpload />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800">
      <Navigation />
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-5xl font-bold text-white mb-8" data-testid="upload-title">Upload Music</h1>

          {/* Upload Fee Status Banner */}
          {feeStatus && (
            <div className={`mb-8 p-6 rounded-xl border ${
              feeStatus.waived 
                ? 'bg-green-900/20 border-green-500' 
                : feeStatus.paid 
                  ? 'bg-cyan-900/20 border-cyan-500'
                  : 'bg-yellow-900/20 border-yellow-500'
            }`}>
              <div className="flex items-center space-x-3">
                <span className="text-3xl">
                  {feeStatus.waived ? '🎉' : feeStatus.paid ? '✅' : '💳'}
                </span>
                <div>
                  <h3 className={`font-bold text-lg ${
                    feeStatus.waived ? 'text-green-400' : feeStatus.paid ? 'text-cyan-400' : 'text-yellow-400'
                  }`}>
                    {feeStatus.message}
                  </h3>
                  {feeStatus.waived && (
                    <p className="text-green-200 text-sm">
                      Account #{feeStatus.account_number} - You're in the first 100 accounts! Upload fee waived forever.
                    </p>
                  )}
                  {feeStatus.paid && !feeStatus.waived && (
                    <p className="text-cyan-200 text-sm">
                      Upload fee paid - You can upload unlimited albums
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Payment Modal */}
          {showPaymentModal && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 max-w-md w-full mx-4 border border-cyan-500">
                <h2 className="text-3xl font-bold text-white mb-4">Album Upload Fee</h2>
                <p className="text-gray-300 mb-6">
                  To upload "<strong className="text-cyan-400">{albumData.title}</strong>" to Origin Audio, a fee of <span className="text-cyan-400 font-bold">$10.00</span> is required per album.
                </p>
                
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 mb-6">
                  <h3 className="text-white font-bold mb-3">What You Get:</h3>
                  <ul className="text-gray-300 space-y-2 text-sm">
                    <li>✅ Upload this album with unlimited tracks</li>
                    <li>✅ Keep 90% of all sales</li>
                    <li>✅ Industry reporting tools</li>
                    <li>✅ Fan data & analytics</li>
                    <li>✅ High-fidelity audio streaming</li>
                  </ul>
                </div>

                <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4 mb-6">
                  <p className="text-yellow-200 text-sm">
                    <strong>Note:</strong> {feeStatus?.upload_fee_waived_forever 
                      ? `First 100 accounts - All uploads FREE forever! This is just a test modal.`
                      : `$10 per album/single uploaded. Each project needs separate payment.`}
                  </p>
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-4 text-sm">
                    {error}
                  </div>
                )}

                <button
                  onClick={handlePayUploadFee}
                  disabled={processingPayment}
                  className="w-full px-6 py-4 bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-500 hover:to-cyan-600 text-white font-bold rounded-lg transition disabled:opacity-50 mb-3"
                  data-testid="pay-upload-fee-button"
                >
                  {processingPayment ? 'Processing Payment...' : 'Pay $10.00 Upload Fee'}
                </button>

                <button
                  onClick={() => navigate('/artist/dashboard')}
                  className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition"
                >
                  Cancel & Go Back
                </button>

                <p className="text-gray-500 text-xs text-center mt-4">
                  Mock payment - No real charges. This will be integrated with Stripe for production.
                </p>
              </div>
            </div>
          )}

          {step === 1 ? (
            <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Step 1: Project Details</h2>

              {/* LEGAL DISCLAIMER */}
              <div className="bg-red-900/20 border-2 border-red-500 rounded-xl p-6 mb-8">
                <div className="flex items-start space-x-3 mb-4">
                  <span className="text-3xl">⚠️</span>
                  <div>
                    <h3 className="text-red-400 font-bold text-xl">LEGAL NOTICE - READ CAREFULLY</h3>
                  </div>
                </div>

                <div className="bg-black/50 rounded-lg p-5 mb-4 max-h-80 overflow-y-auto">
                  <div className="text-gray-300 space-y-3 text-sm">
                    <p className="font-semibold">By uploading, you certify:</p>
                    <p>✓ You OWN all rights to this music OR have obtained all necessary licenses</p>
                    <p>✓ This content does NOT infringe on any copyrights or third-party rights</p>
                    <p>✓ You AGREE to indemnify Origin Audio from ANY claims or legal costs</p>
                    <p>✓ Origin Audio is NOT liable for copyright disputes or claims</p>
                    <p>✓ Violations may result in account termination and legal action</p>
                    
                    <div className="bg-yellow-900/40 border border-yellow-500 rounded p-3 mt-3">
                      <p className="text-yellow-300 font-bold text-xs">⚠️ WARNING: Uploading copyrighted material without authorization is ILLEGAL and may result in severe legal consequences.</p>
                    </div>
                  </div>
                </div>

                <label className="flex items-start space-x-3 cursor-pointer bg-gray-900 border-2 border-cyan-500 rounded-lg p-4">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="w-6 h-6 rounded text-cyan-600 mt-1 cursor-pointer"
                    data-testid="terms-checkbox"
                  />
                  <div>
                    <p className="text-white font-bold">I ACCEPT - I own the rights to this music</p>
                    <p className="text-gray-400 text-xs mt-1">I agree to indemnify Origin Audio from all liability</p>
                  </div>
                </label>

                {!termsAccepted && (
                  <p className="text-red-400 text-sm font-semibold mt-3">🔒 Must accept to continue</p>
                )}
              </div>

              <form onSubmit={handleAlbumSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-300 mb-2">Project Type *</label>
                  <select
                    value={albumData.release_type}
                    onChange={(e) => setAlbumData({ ...albumData, release_type: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    data-testid="release-type-select"
                  >
                    <option value="album">Album - Official Release (with industry reporting)</option>
                    <option value="mixtape">Mixtape - Direct-to-Fans (no codes needed)</option>
                  </select>
                  {albumData.release_type === 'mixtape' && (
                    <p className="text-cyan-400 text-sm mt-2">
                      🎤 Mixtapes are simpler - no UPC/ISRC codes needed! Just you and your fans.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">{albumData.release_type === 'mixtape' ? 'Mixtape' : 'Album'} Title *</label>
                  <input
                    type="text"
                    value={albumData.title}
                    onChange={(e) => setAlbumData({ ...albumData, title: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    required
                    data-testid="album-title-input"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Description</label>
                  <textarea
                    value={albumData.description}
                    onChange={(e) => setAlbumData({ ...albumData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400 h-32"
                    data-testid="album-description-input"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Price (USD) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={albumData.price}
                    onChange={(e) => setAlbumData({ ...albumData, price: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    required
                    data-testid="album-price-input"
                  />
                  <p className="text-sm text-green-400 mt-2">You'll receive 90% (${(parseFloat(albumData.price) * 0.9).toFixed(2)})</p>
                </div>

                {albumData.release_type === 'album' && (
                  <div className="border-t border-gray-700 pt-6 mt-6">
                    <h3 className="text-xl font-bold text-cyan-400 mb-4">📊 Industry Reporting Metadata</h3>
                    <p className="text-sm text-gray-400 mb-4">Required for Billboard charts and industry sales tracking</p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-gray-300 mb-2">UPC Code (12 digits)</label>
                        <input
                          type="text"
                          value={albumData.upc_code}
                          onChange={(e) => setAlbumData({ ...albumData, upc_code: e.target.value })}
                          placeholder="e.g., 123456789012"
                          maxLength="12"
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                          data-testid="album-upc-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Universal Product Code - Get from GS1 or your distributor</p>
                      </div>

                      <div>
                        <label className="block text-gray-300 mb-2">Label Name</label>
                        <input
                          type="text"
                          value={albumData.label_name}
                          onChange={(e) => setAlbumData({ ...albumData, label_name: e.target.value })}
                          placeholder="Independent / Your Label Name"
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                          data-testid="album-label-input"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-300 mb-2">Territory</label>
                        <select
                          value={albumData.territory}
                          onChange={(e) => setAlbumData({ ...albumData, territory: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                          data-testid="album-territory-input"
                        >
                          <option value="US">United States</option>
                          <option value="CA">Canada</option>
                          <option value="UK">United Kingdom</option>
                          <option value="WW">Worldwide</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {albumData.release_type === 'mixtape' && (
                  <div className="bg-cyan-900/20 border border-cyan-600 rounded-lg p-6">
                    <h3 className="text-cyan-400 font-bold mb-2">🎤 Mixtape Mode</h3>
                    <p className="text-gray-300 text-sm">
                      No UPC/ISRC codes needed! Mixtapes are direct-to-fan releases. 
                      Focus on your music, skip the industry paperwork. Sales won't count toward Billboard charts, 
                      but you still keep 90% and your fans own the music forever.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-gray-300 mb-2">Cover Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setCoverFile(e.target.files[0])}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    data-testid="album-cover-input"
                  />
                </div>

                {/* PRE-SALE SETTINGS - Only for Artists and Labels, NOT AI Creators */}
                {user && user.role !== 'ai_creator' && (
                  <div className="border-t border-gray-700 pt-6 mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-cyan-400">🎯 Pre-Sale Options</h3>
                        <p className="text-sm text-gray-400 mt-1">Let fans purchase before release - they can preview but can't download until released</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={albumData.is_presale}
                          onChange={(e) => setAlbumData({ ...albumData, is_presale: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-cyan-600"></div>
                        <span className="ml-3 text-sm font-medium text-gray-300">
                          {albumData.is_presale ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    </div>

                    {albumData.is_presale && (
                      <div className="bg-cyan-900/20 border border-cyan-600 rounded-lg p-6 space-y-4">
                        <div>
                          <label className="block text-gray-300 mb-2">Release Date *</label>
                          <input
                            type="datetime-local"
                            value={albumData.presale_release_date}
                            onChange={(e) => setAlbumData({ ...albumData, presale_release_date: e.target.value })}
                            className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                            required={albumData.is_presale}
                          />
                          <p className="text-xs text-gray-400 mt-2">
                            Fans can purchase now but won't be able to download until this date
                          </p>
                        </div>

                        <div className="bg-gray-900 border border-cyan-700 rounded p-4">
                          <h4 className="text-cyan-400 font-bold text-sm mb-2">🎧 How Pre-Sale Works:</h4>
                          <ul className="text-gray-300 text-sm space-y-1">
                            <li>✓ Fans can preview 30-second clips now</li>
                            <li>✓ They can purchase at full price</li>
                            <li>✓ Album shows as "Purchased" in their Vault</li>
                            <li>✓ Full streaming/download unlocks on release date</li>
                            <li>✓ You get paid immediately when they purchase</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Creator Info */}
                {user && user.role === 'ai_creator' && (
                  <div className="border-t border-purple-700 pt-6 mt-6">
                    <div className="bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border border-purple-500 rounded-lg p-6">
                      <h3 className="text-xl font-bold text-purple-400 mb-3">🤖 AI Music Upload</h3>
                      <div className="space-y-2 text-purple-200">
                        <p>• <strong className="text-purple-400">Upload Fee:</strong> $9.00 per project</p>
                        <p>• <strong className="text-purple-400">Revenue Split:</strong> 80% to you, 20% platform</p>
                        <p>• <strong className="text-purple-400">Market:</strong> Your music appears in AI Marketplace</p>
                        <p className="text-purple-300 text-sm mt-3">⚠️ Pre-sale feature not available for AI music</p>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={uploading || !termsAccepted}
                  className="w-full px-6 py-3 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-cyan-600 hover:to-cyan-700 text-white font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed border border-gray-600"
                  data-testid="create-album-button"
                >
                  {uploading ? 'Creating...' : 'Create Album & Continue'}
                </button>
                
                {!termsAccepted && (
                  <p className="text-center text-red-400 text-sm font-semibold mt-2">
                    ⚠️ Please accept the legal terms above to proceed
                  </p>
                )}
              </form>
            </div>
          ) : (
            <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Step 2: Upload Tracks</h2>
              
              <form onSubmit={handleTrackUpload} className="space-y-6 mb-8">
                <div>
                  <label className="block text-gray-300 mb-2">Track Title *</label>
                  <input
                    type="text"
                    name="trackTitle"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    required
                    data-testid="track-title-input"
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">ISRC Code (Optional)</label>
                  <input
                    type="text"
                    name="isrcCode"
                    placeholder="e.g., USUM71234567"
                    maxLength="12"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    data-testid="track-isrc-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">International Standard Recording Code - Format: CC-XXX-YY-NNNNN</p>
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">Audio File (MP3, WAV, FLAC) *</label>
                  <input
                    type="file"
                    name="audioFile"
                    accept=".mp3,.wav,.flac"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                    required
                    data-testid="track-file-input"
                  />
                  <p className="text-sm text-gray-400 mt-2">High-fidelity formats supported</p>
                </div>

                <button
                  type="submit"
                  disabled={uploading}
                  className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-lg transition disabled:opacity-50 border border-gray-600"
                  data-testid="upload-track-button"
                >
                  {uploading ? 'Uploading...' : 'Add Track'}
                </button>
              </form>

              {tracks.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-white mb-4">Uploaded Tracks ({tracks.length})</h3>
                  <div className="space-y-2">
                    {tracks.map((track) => (
                      <div key={track.id} className="flex justify-between items-center bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                        <span className="text-white">
                          {track.track_number}. {track.title}
                        </span>
                        <span className="text-cyan-400 text-sm uppercase">{track.file_type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-8 space-y-4">
                <button
                  onClick={() => setStep(3)}
                  disabled={tracks.length === 0}
                  className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-lg transition text-lg shadow-lg disabled:opacity-50"
                  data-testid="configure-singles-button"
                >
                  💿 Next: Configure Singles Pricing →
                </button>
                <button
                  onClick={finishUpload}
                  className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-lg transition"
                  data-testid="skip-singles-button"
                >
                  Skip & Finish Later
                </button>
                <p className="text-center text-gray-400 text-sm">
                  💡 Tip: Singles pricing helps maximize sales and gives fans options!
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Singles Pricing Configuration */}
          {step === 3 && (
            <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-white mb-2">💿 Configure Singles Pricing</h2>
              <p className="text-gray-400 mb-6">
                Select which tracks fans can buy individually. Set prices for each single (must be ≤ album price of ${albumData.price}).
              </p>

              <div className="space-y-4 mb-8">
                {tracks.map((track, index) => {
                  const settings = singlesSettings[track.id] || { enabled: false, price: '' };
                  
                  return (
                    <div key={track.id} className="bg-gray-800/50 border-2 border-gray-700 rounded-lg p-6 hover:border-cyan-500 transition">
                      <div className="flex items-start space-x-4">
                        <input
                          type="checkbox"
                          checked={settings.enabled}
                          onChange={(e) => {
                            const suggestedPrice = (parseFloat(albumData.price) * 0.7).toFixed(2);
                            setSinglesSettings({
                              ...singlesSettings,
                              [track.id]: { enabled: e.target.checked, price: e.target.checked ? suggestedPrice : '' }
                            });
                          }}
                          className="w-6 h-6 rounded border-gray-600 text-cyan-600 focus:ring-cyan-500 cursor-pointer mt-1"
                        />

                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-white font-bold text-lg">
                              {index + 1}. {track.title}
                            </h3>
                            <span className="text-cyan-400 text-sm uppercase px-3 py-1 bg-cyan-900/30 rounded-full">{track.file_type}</span>
                          </div>

                          {settings.enabled ? (
                            <div className="mt-3">
                              <label className="block text-gray-300 mb-2 font-semibold">Price for this single:</label>
                              <div className="flex items-center space-x-3">
                                <span className="text-gray-400 text-xl">$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0.01"
                                  max={parseFloat(albumData.price)}
                                  value={settings.price}
                                  onChange={(e) => {
                                    setSinglesSettings({
                                      ...singlesSettings,
                                      [track.id]: { ...settings, price: e.target.value }
                                    });
                                  }}
                                  placeholder={`Max $${albumData.price}`}
                                  className="flex-1 px-4 py-3 bg-gray-700 border-2 border-gray-600 rounded-lg text-white text-lg font-bold focus:outline-none focus:border-cyan-400"
                                />
                                <span className="text-gray-400 text-sm px-3 py-2 bg-gray-700 rounded-lg whitespace-nowrap">
                                  {settings.price ? `${((parseFloat(settings.price) / parseFloat(albumData.price)) * 100).toFixed(0)}% of album` : '—'}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-gray-500 text-sm italic">Album-only • Fans must buy full album</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500 rounded-xl p-6 mb-6">
                <h3 className="text-purple-300 font-bold mb-3">💡 Pricing Strategies:</h3>
                <div className="grid md:grid-cols-2 gap-3 text-sm text-gray-300">
                  <div>• <strong>60-80%</strong> drives album sales</div>
                  <div>• <strong>All singles</strong> = max flexibility</div>
                  <div>• <strong>1-2 singles</strong> = teaser strategy</div>
                  <div>• <strong>Update anytime</strong> from dashboard</div>
                </div>
              </div>

              <div className="bg-cyan-900/20 border border-cyan-500 rounded-lg p-4 mb-6 text-center">
                <span className="text-cyan-300 font-bold text-lg">
                  {Object.values(singlesSettings).filter(s => s.enabled).length} / {tracks.length} tracks as singles
                </span>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={async () => {
                    try {
                      const updates = Object.entries(singlesSettings)
                        .filter(([_, settings]) => settings.enabled && settings.price)
                        .map(([trackId, settings]) => 
                          axios.put(`${API}/tracks/${trackId}/single-settings`,
                            { available_as_single: true, single_price: parseFloat(settings.price) },
                            { headers: { Authorization: `Bearer ${token}` } }
                          )
                        );

                      if (updates.length > 0) {
                        await Promise.all(updates);
                        alert(`✅ ${updates.length} singles configured!`);
                      }
                      navigate('/artist/dashboard');
                    } catch (error) {
                      alert('Settings saved. Check dashboard.');
                      navigate('/artist/dashboard');
                    }
                  }}
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-bold rounded-lg transition text-lg"
                >
                  🚀 Save & Publish
                </button>
                <button onClick={finishUpload} className="px-6 py-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg">
                  Skip
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// Artist Dashboard
const ArtistDashboard = () => {
  const { token, user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [reportSummary, setReportSummary] = useState(null);
  const [feeStatus, setFeeStatus] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [themeColor, setThemeColor] = useState(user?.dashboard_theme || 'default');
  const [updatingTheme, setUpdatingTheme] = useState(false);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [profileImage, setProfileImage] = useState(user?.profile_image || null);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [showSinglesModal, setShowSinglesModal] = useState(false);
  const [selectedAlbumForSingles, setSelectedAlbumForSingles] = useState(null);
  const [trackSinglesSettings, setTrackSinglesSettings] = useState({});

  // Theme presets with gradient colors
  const themePresets = {
    default: { from: 'from-black', via: 'via-gray-900', to: 'to-gray-800', name: 'Default Dark' },
    purple: { from: 'from-purple-900', via: 'via-purple-800', to: 'to-black', name: 'Purple Haze' },
    blue: { from: 'from-blue-900', via: 'via-blue-800', to: 'to-black', name: 'Ocean Blue' },
    green: { from: 'from-green-900', via: 'via-green-800', to: 'to-black', name: 'Forest Green' },
    red: { from: 'from-red-900', via: 'via-red-800', to: 'to-black', name: 'Ruby Red' },
    orange: { from: 'from-orange-900', via: 'via-orange-800', to: 'to-black', name: 'Sunset Orange' },
    pink: { from: 'from-pink-900', via: 'via-pink-800', to: 'to-black', name: 'Hot Pink' },
    cyan: { from: 'from-cyan-900', via: 'via-cyan-800', to: 'to-black', name: 'Electric Cyan' },
    teal: { from: 'from-teal-900', via: 'via-teal-800', to: 'to-black', name: 'Teal Wave' }
  };

  useEffect(() => {
    fetchData();
    if (user?.dashboard_theme) {
      setThemeColor(user.dashboard_theme);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, albumsRes, reportRes, feeRes, referralRes] = await Promise.all([
        axios.get(`${API}/analytics/sales`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/artists/${user.id}/albums`),
        axios.get(`${API}/reports/sales/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/payment/upload-fee/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/referrals/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAnalytics(analyticsRes.data);
      setAlbums(albumsRes.data);
      setReportSummary(reportRes.data);
      setFeeStatus(feeRes.data);
      setReferralStats(referralRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Admin cleanup function - removes test albums
  const handleCleanupTestData = async () => {
    if (!window.confirm('🗑️ Delete all test albums?\n\nThis will remove:\n• Gary Talley albums\n• A+ albums\n• Nicholas Carr albums\n\nYour albums will NOT be affected.\n\nContinue?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.delete(`${API}/admin/cleanup-test-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const summary = response.data.deleted_albums.length > 0
        ? response.data.deleted_albums.map(a => `• ${a.title} by ${a.artist}`).join('\n')
        : 'No test albums found';
      
      alert(`✅ Cleanup Complete!\n\n📊 Deleted:\n• ${response.data.total_albums_deleted} albums\n• ${response.data.total_tracks_deleted} tracks\n\n${summary}\n\nRefreshing...`);
      
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      alert('❌ Cleanup failed: ' + (err.response?.data?.detail || err.message));
      setLoading(false);
    }
  };

  const handleThemeChange = async (newTheme) => {
    setUpdatingTheme(true);
    try {
      await axios.post(`${API}/dashboard/theme`, 
        { theme_color: newTheme },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setThemeColor(newTheme);
      setShowCustomizeModal(false);
      alert('Dashboard theme updated successfully!');
    } catch (error) {
      console.error('Failed to update theme:', error);
      alert(error.response?.data?.detail || 'Failed to update theme');
    } finally {
      setUpdatingTheme(false);
    }
  };

  const handleResetTheme = async () => {
    if (!confirm('Reset to default theme?')) return;

    try {
      await axios.delete(`${API}/dashboard/theme`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setThemeColor('default');
      setShowCustomizeModal(false);
      alert('Theme reset to default!');
    } catch (error) {
      console.error('Failed to reset theme:', error);
      alert('Failed to reset theme');
    }
  };

  const handleProfilePictureUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a valid image (JPEG, PNG, or WebP)');
      return;
    }
    
    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image size must be less than 2MB');
      return;
    }

    setUploadingProfile(true);
    try {
      const formData = new FormData();
      formData.append('picture', file);

      const response = await axios.post(`${API}/profile/picture`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setProfileImage(`${BACKEND_URL}${response.data.profile_image}`);
      alert('Profile picture updated successfully!');
      window.location.reload();
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      alert(error.response?.data?.detail || 'Failed to upload profile picture');
    } finally {
      setUploadingProfile(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!confirm('Remove your profile picture?')) return;

    try {
      await axios.delete(`${API}/profile/picture`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setProfileImage(null);
      alert('Profile picture removed!');
      window.location.reload();
    } catch (error) {
      console.error('Failed to remove profile picture:', error);
      alert('Failed to remove profile picture');
    }
  };

  const handleTrackSingleToggle = (trackId, enabled, price) => {
    setTrackSinglesSettings(prev => ({
      ...prev,
      [trackId]: { enabled, price: price || '' }
    }));
  };

  const handleSaveSinglesSettings = async () => {
    try {
      const updates = Object.entries(trackSinglesSettings).map(([trackId, settings]) => {
        return axios.put(
          `${API}/tracks/${trackId}/single-settings`,
          {
            available_as_single: settings.enabled,
            single_price: settings.enabled ? parseFloat(settings.price) : null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      });

      await Promise.all(updates);
      alert('Singles settings saved successfully!');
      setShowSinglesModal(false);
      setTrackSinglesSettings({});
      fetchData(); // Refresh dashboard data
    } catch (error) {
      console.error('Error saving singles settings:', error);
      alert(error.response?.data?.detail || 'Failed to save settings');
    }
  };

  const exportSalesReport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/reports/sales/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `origin_audio_sales_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export report:', error);
      alert('Failed to export sales report');
    } finally {
      setExporting(false);
    }
  };

  const copyReferralCode = () => {
    if (referralStats?.referral_code) {
      const referralLink = `${window.location.origin}?ref=${referralStats.referral_code}`;
      navigator.clipboard.writeText(referralLink);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-20 text-center text-white">Loading...</div>;
  }

  const currentTheme = themePresets[themeColor] || themePresets.default;

  return (
    <div className={`min-h-screen bg-gradient-to-br ${currentTheme.from} ${currentTheme.via} ${currentTheme.to} transition-all duration-500`}>
      <Navigation />
      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="flex justify-between items-start mb-8">
          <div className="flex items-center space-x-6">
            {/* Profile Picture */}
            <div className="relative">
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-cyan-500 bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                {profileImage || user.profile_image ? (
                  <img 
                    src={profileImage || (user.profile_image ? `${BACKEND_URL}${user.profile_image}` : null)} 
                    alt={user.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-4xl">{user.role === 'label' ? '🏢' : '🎤'}</span>
                )}
              </div>
              <label className="absolute bottom-0 right-0 bg-cyan-600 hover:bg-cyan-700 rounded-full p-2 cursor-pointer shadow-lg transition">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleProfilePictureUpload(file);
                  }}
                  className="hidden"
                  disabled={uploadingProfile}
                />
                <span className="text-white text-sm">📷</span>
              </label>
            </div>
            
            <div>
              <h1 className="text-5xl font-bold text-white mb-2" data-testid="dashboard-title">
                {user.role === 'label' ? 'Label' : 'Artist'} Dashboard
              </h1>
              <p className="text-gray-400 text-xl">Welcome back, {user.name}</p>
              {profileImage || user.profile_image ? (
                <button
                  onClick={handleRemoveProfilePicture}
                  className="text-red-400 hover:text-red-300 text-sm mt-1"
                >
                  Remove profile picture
                </button>
              ) : null}
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={handleCleanupTestData}
              disabled={loading}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold rounded-lg transition shadow-lg flex items-center space-x-2"
              title="Remove test albums (Gary Talley, A+, Nicholas Carr)"
            >
              <span>🗑️</span>
              <span>{loading ? 'Cleaning...' : 'Cleanup Test Albums'}</span>
            </button>
            <button
              onClick={() => setShowCustomizeModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-lg transition shadow-lg flex items-center space-x-2"
              data-testid="customize-dashboard-button"
            >
              <span>🎨</span>
              <span>Customize Theme</span>
            </button>
          </div>
        </div>

        {/* Upload Fee Status */}
        {feeStatus && feeStatus.waived && (
          <div className="bg-gradient-to-r from-green-900/30 to-green-800/30 border border-green-500 rounded-xl p-6 mb-8">
            <div className="flex items-center space-x-4">
              <span className="text-4xl">🎉</span>
              <div>
                <h3 className="text-green-400 font-bold text-xl">Welcome, Early Adopter!</h3>
                <p className="text-green-200">
                  Account #{feeStatus.account_number} - You're in the first 100 accounts! Upload fee permanently waived.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Revenue Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gradient-to-br from-green-700 to-green-900 rounded-xl p-6 border border-green-600">
            <div className="text-green-200 text-sm mb-2">Your Revenue (90%)</div>
            <div className="text-4xl font-bold text-white" data-testid="artist-revenue">${analytics?.artist_revenue?.toFixed(2) || '0.00'}</div>
          </div>
          <div className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl p-6 border border-gray-600">
            <div className="text-gray-300 text-sm mb-2">Total Sales</div>
            <div className="text-4xl font-bold text-white" data-testid="total-sales">{analytics?.total_sales || 0}</div>
          </div>
          <div className="bg-gradient-to-br from-cyan-700 to-cyan-900 rounded-xl p-6 border border-cyan-600">
            <div className="text-cyan-200 text-sm mb-2">Total Fans</div>
            <div className="text-4xl font-bold text-white" data-testid="total-fans">{analytics?.fans?.length || 0}</div>
          </div>
        </div>

        {/* Referral Program Section */}
        {referralStats && (
          <div className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 border-2 border-yellow-500 rounded-xl p-8 mb-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 text-9xl opacity-10">💰</div>
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-bold text-yellow-400 mb-2">🎁 Artist Referral Program</h2>
                  <p className="text-yellow-200 text-lg">Earn 1% of every sale your referred artists make — FOR LIFE!</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-gray-900/80 border border-yellow-600 rounded-lg p-6 text-center">
                  <div className="text-yellow-400 text-sm mb-2">Total Referrals</div>
                  <div className="text-4xl font-bold text-white">{referralStats.total_referrals}</div>
                </div>
                <div className="bg-gray-900/80 border border-yellow-600 rounded-lg p-6 text-center">
                  <div className="text-yellow-400 text-sm mb-2">Lifetime Earnings</div>
                  <div className="text-4xl font-bold text-green-400">${referralStats.total_commission_earned.toFixed(2)}</div>
                </div>
                <div className="bg-gray-900/80 border border-yellow-600 rounded-lg p-6 text-center">
                  <div className="text-yellow-400 text-sm mb-2">Commission Rate</div>
                  <div className="text-4xl font-bold text-yellow-400">1%</div>
                  <div className="text-xs text-yellow-300">LIFETIME</div>
                </div>
              </div>

              <div className="bg-black/30 border border-yellow-600 rounded-xl p-6 mb-6">
                <h3 className="text-xl font-bold text-yellow-400 mb-4">Your Referral Code</h3>
                <div className="flex items-center space-x-4">
                  <div className="flex-1 bg-gray-900 border border-gray-700 rounded-lg p-4">
                    <code className="text-3xl font-mono font-bold text-cyan-400" data-testid="referral-code">
                      {referralStats.referral_code}
                    </code>
                  </div>
                  <button
                    onClick={copyReferralCode}
                    className="px-6 py-4 bg-cyan-600 hover:bg-cyan-700 text-white font-bold rounded-lg transition"
                    data-testid="copy-referral-button"
                  >
                    {copiedCode ? '✅ Copied!' : '📋 Copy Link'}
                  </button>
                </div>
                <p className="text-yellow-200 text-sm mt-3">
                  Share this link: <code className="bg-black/50 px-2 py-1 rounded">{window.location.origin}?ref={referralStats.referral_code}</code>
                </p>
              </div>

              <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-6">
                <h3 className="text-lg font-bold text-yellow-400 mb-3">💡 How It Works:</h3>
                <ul className="text-yellow-200 space-y-2">
                  <li>✅ Share your referral code with other artists</li>
                  <li>✅ When they sign up and start selling music</li>
                  <li>✅ You earn <strong>1% of every sale they make</strong></li>
                  <li>✅ <strong>FOREVER</strong> - lifetime passive income!</li>
                  <li>✅ They still keep their 90% revenue share</li>
                </ul>
              </div>

              {referralStats.referred_artists && referralStats.referred_artists.length > 0 && (
                <div className="mt-6 bg-black/30 border border-yellow-600 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-yellow-400 mb-4">Your Referred Artists</h3>
                  <div className="space-y-3">
                    {referralStats.referred_artists.map((artist, index) => (
                      <div key={index} className="bg-gray-900/60 border border-gray-700 rounded-lg p-4 flex justify-between items-center">
                        <div>
                          <div className="text-white font-bold">{artist.name}</div>
                          <div className="text-gray-400 text-sm">{artist.number_of_sales} sales</div>
                        </div>
                        <div className="text-right">
                          <div className="text-green-400 font-bold text-lg">${artist.commission_earned.toFixed(2)}</div>
                          <div className="text-gray-400 text-xs">earned</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 90/10 Split Visualization */}
        <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">Revenue Split</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex justify-between text-white mb-2">
                <span>Artist (You)</span>
                <span className="font-bold">${analytics?.artist_revenue?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="h-12 bg-green-600 rounded-lg flex items-center justify-center text-white font-bold">
                90%
              </div>
            </div>
            <div>
              <div className="flex justify-between text-white mb-2">
                <span>Platform Fee</span>
                <span className="font-bold">${analytics?.platform_fee?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="h-12 bg-gray-700 rounded-lg flex items-center justify-center text-white font-bold">
                10%
              </div>
            </div>
          </div>
          <p className="text-gray-400 text-sm mt-4">
            Origin Audio takes only 10%. You keep 90% of every sale.
          </p>
        </div>

        {/* Industry Reports Section */}
        {reportSummary && (
          <div className="bg-gradient-to-br from-cyan-900/30 to-gray-900/80 backdrop-blur-sm border border-cyan-700 rounded-xl p-8 mb-12">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">📊 Industry Sales Reporting</h2>
                <p className="text-gray-400">Export data for Luminate/SoundScan, Billboard, and RIAA</p>
              </div>
              <button
                onClick={exportSalesReport}
                disabled={exporting || reportSummary.total_sales === 0}
                className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-500 hover:to-cyan-600 text-white font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="export-sales-report-button"
              >
                {exporting ? 'Exporting...' : '📥 Export CSV Report'}
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <div className="text-gray-400 text-sm mb-1">Metadata Completion</div>
                <div className="text-3xl font-bold text-cyan-400">{reportSummary.metadata_completion_percentage}%</div>
              </div>
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <div className="text-gray-400 text-sm mb-1">Albums with UPC</div>
                <div className="text-2xl font-bold text-white">
                  {reportSummary.albums_with_upc} / {reportSummary.total_albums}
                </div>
              </div>
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                <div className="text-gray-400 text-sm mb-1">Tracks with ISRC</div>
                <div className="text-2xl font-bold text-white">
                  {reportSummary.tracks_with_isrc} / {reportSummary.total_tracks}
                </div>
              </div>
            </div>

            {!reportSummary.reporting_ready && (
              <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">⚠️</span>
                  <div>
                    <h3 className="text-yellow-400 font-bold mb-2">Action Required: Complete Metadata</h3>
                    <p className="text-yellow-200 text-sm mb-2">
                      To report sales to industry agencies, you need:
                    </p>
                    <ul className="text-yellow-200 text-sm space-y-1 ml-4">
                      {reportSummary.albums_without_upc > 0 && (
                        <li>• Add UPC codes to {reportSummary.albums_without_upc} album(s)</li>
                      )}
                      {reportSummary.tracks_without_isrc > 0 && (
                        <li>• Add ISRC codes to {reportSummary.tracks_without_isrc} track(s)</li>
                      )}
                    </ul>
                    <p className="text-yellow-200 text-sm mt-3">
                      <strong>Get UPC:</strong> GS1.org or your distributor • <strong>Get ISRC:</strong> usisrc.org or your distributor
                    </p>
                  </div>
                </div>
              </div>
            )}

            {reportSummary.reporting_ready && (
              <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <h3 className="text-green-400 font-bold">Ready for Industry Reporting!</h3>
                    <p className="text-green-200 text-sm">
                      All albums and tracks have proper metadata. Export your sales report and submit to Luminate/SoundScan.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 border-t border-gray-700 pt-6">
              <h3 className="text-white font-bold mb-3">About Industry Reporting:</h3>
              <ul className="text-gray-400 text-sm space-y-2">
                <li>• <strong className="text-cyan-400">Luminate (SoundScan)</strong> - Powers Billboard charts, requires business account (~$500-2K/mo)</li>
                <li>• <strong className="text-cyan-400">RIAA Certifications</strong> - Submit data for Gold/Platinum awards (500K+ sales)</li>
                <li>• <strong className="text-cyan-400">CSV Export</strong> - Industry-standard format ready for manual submission</li>
              </ul>
            </div>
          </div>
        )}

        {/* Sales by Album */}
        {analytics?.sales_by_album && analytics.sales_by_album.length > 0 && (
          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">Sales by Album</h2>
            <div className="space-y-4">
              {analytics.sales_by_album.map((album, index) => (
                <div key={index} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-bold">{album.album_title}</span>
                    <div className="text-right">
                      <div className="text-green-400 font-bold">${album.revenue.toFixed(2)}</div>
                      <div className="text-gray-400 text-sm">{album.sales_count} sales</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fan List */}
        {analytics?.fans && analytics.fans.length > 0 && (
          <div className="bg-gray-900/80 backdrop-blur-sm border border-gray-700 rounded-xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Your Fans</h2>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="fans-table">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left text-gray-400 py-3 px-4">Name</th>
                    <th className="text-left text-gray-400 py-3 px-4">Email</th>
                    <th className="text-right text-gray-400 py-3 px-4">Purchases</th>
                    <th className="text-right text-gray-400 py-3 px-4">Total Spent</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.fans.map((fan, index) => (
                    <tr key={index} className="border-b border-gray-800">
                      <td className="text-white py-3 px-4">{fan.name}</td>
                      <td className="text-gray-400 py-3 px-4">{fan.email}</td>
                      <td className="text-white text-right py-3 px-4">{fan.total_purchases}</td>
                      <td className="text-green-400 text-right py-3 px-4 font-bold">${fan.total_spent.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* My Albums */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-white mb-6">My Albums</h2>
          {albums.length === 0 ? (
            <div className="text-center text-gray-400 py-12">
              No albums yet. <Link to="/artist/upload" className="text-cyan-400 underline">Upload your first album</Link>
            </div>
          ) : (
            <div className="space-y-6">
              {albums.map((album) => (
                <div key={album.id} className="bg-gray-900/80 border border-gray-700 rounded-xl p-6">
                  <div className="flex items-start space-x-4">
                    {/* Album Cover */}
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                      {album.cover_url ? (
                        <img src={`${BACKEND_URL}${album.cover_url}`} alt={album.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-3xl">🎵</div>
                      )}
                    </div>
                    
                    {/* Album Info */}
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-1">{album.title}</h3>
                      <p className="text-gray-400 text-sm mb-2">Album Price: ${album.price.toFixed(2)}</p>
                      <p className="text-gray-500 text-sm">{album.tracks?.length || 0} tracks</p>
                      
                      {/* Track Singles Management */}
                      {album.tracks && album.tracks.length > 0 && (
                        <div className="mt-4">
                          <button
                            onClick={() => {
                              const albumWithTracks = albums.find(a => a.id === album.id);
                              setSelectedAlbumForSingles(albumWithTracks);
                              setShowSinglesModal(true);
                            }}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg transition text-sm"
                          >
                            💿 Manage Singles ({album.tracks.filter(t => t.available_as_single).length}/{album.tracks.length})
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Singles Management Modal */}
      {showSinglesModal && selectedAlbumForSingles && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowSinglesModal(false)}>
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 max-w-3xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-white">💿 Manage Singles - {selectedAlbumForSingles.title}</h2>
              <button
                onClick={() => setShowSinglesModal(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <p className="text-gray-400 mb-6">
              Choose which tracks to sell individually and set their prices. Singles cannot be priced higher than the full album (${selectedAlbumForSingles.price.toFixed(2)}).
            </p>

            {/* Track List */}
            <div className="space-y-4 mb-6">
              {selectedAlbumForSingles.tracks.map((track) => {
                const settings = trackSinglesSettings[track.id] || { 
                  enabled: track.available_as_single || false, 
                  price: track.single_price || '' 
                };

                return (
                  <div key={track.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                    <div className="flex items-start space-x-4">
                      <div className="flex items-center space-x-3 flex-1">
                        <input
                          type="checkbox"
                          checked={settings.enabled}
                          onChange={(e) => handleTrackSingleToggle(track.id, e.target.checked, settings.price)}
                          className="w-5 h-5 rounded border-gray-600 text-cyan-600 focus:ring-cyan-500"
                        />
                        <div className="flex-1">
                          <p className="text-white font-bold">{track.track_number}. {track.title}</p>
                          <p className="text-gray-500 text-sm">{track.file_type.toUpperCase()}</p>
                        </div>
                      </div>

                      {settings.enabled && (
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            max={selectedAlbumForSingles.price}
                            value={settings.price}
                            onChange={(e) => handleTrackSingleToggle(track.id, true, e.target.value)}
                            placeholder="Price"
                            className="w-24 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleSaveSinglesSettings}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-bold rounded-lg transition"
              >
                Save Singles Settings
              </button>
              <button
                onClick={() => setShowSinglesModal(false)}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-lg transition"
              >
                Cancel
              </button>
            </div>

            <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-4 mt-6">
              <h3 className="text-purple-400 font-bold mb-2">💡 Singles Strategy:</h3>
              <ul className="text-purple-200 text-sm space-y-1">
                <li>• Price singles at 60-80% of album price for best value</li>
                <li>• Popular tracks can drive album sales</li>
                <li>• Singles give fans flexible purchase options</li>
                <li>• You can update prices anytime</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Customize Dashboard Modal */}
      {showCustomizeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowCustomizeModal(false)}>
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 max-w-3xl w-full border border-gray-700 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-white">🎨 Customize Dashboard Theme</h2>
              <button
                onClick={() => setShowCustomizeModal(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>

            <p className="text-gray-400 mb-6">Choose a color theme that matches your style</p>

            {/* Theme Preview Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {Object.entries(themePresets).map(([key, theme]) => (
                <button
                  key={key}
                  onClick={() => handleThemeChange(key)}
                  disabled={updatingTheme}
                  className={`relative rounded-xl overflow-hidden border-4 transition-all ${
                    themeColor === key 
                      ? 'border-cyan-400 scale-105 shadow-lg shadow-cyan-400/50' 
                      : 'border-gray-700 hover:border-gray-500'
                  } ${updatingTheme ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  data-testid={`theme-${key}`}
                >
                  <div className={`h-32 bg-gradient-to-br ${theme.from} ${theme.via} ${theme.to} flex items-center justify-center`}>
                    {themeColor === key && (
                      <div className="absolute top-2 right-2 bg-cyan-400 text-black rounded-full p-1">
                        <span className="text-sm">✓</span>
                      </div>
                    )}
                    <div className="text-center">
                      <div className="text-white font-bold text-lg mb-1">{theme.name}</div>
                      <div className="text-gray-300 text-xs">Click to apply</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {updatingTheme && (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
                <p className="text-gray-400 mt-2">Applying theme...</p>
              </div>
            )}

            {themeColor !== 'default' && !updatingTheme && (
              <button
                onClick={handleResetTheme}
                className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-lg transition mt-4"
                data-testid="reset-theme-button"
              >
                Reset to Default Theme
              </button>
            )}

            <div className="bg-purple-900/20 border border-purple-700 rounded-lg p-4 mt-6">
              <h3 className="text-purple-400 font-bold mb-2">✨ Theme Benefits:</h3>
              <ul className="text-purple-200 text-sm space-y-1">
                <li>• Express your unique brand identity</li>
                <li>• Create a memorable dashboard experience</li>
                <li>• Match your album artwork aesthetic</li>
                <li>• Stand out with your personal style</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Guest Download Page
const GuestDownload = () => {
  const [loading, setLoading] = useState(true);
  const [downloadLinks, setDownloadLinks] = useState([]);
  const [error, setError] = useState('');
  const [copyrightNotice, setCopyrightNotice] = useState('');

  useEffect(() => {
    fetchGuestDownload();
  }, []);

  const fetchGuestDownload = async () => {
    const params = new URLSearchParams(window.location.search);
    const purchaseId = params.get('purchase_id');
    const email = params.get('email');

    if (!purchaseId || !email) {
      setError('Invalid download link. Please check your email for the correct link.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/downloads/guest/${purchaseId}`, {
        params: { email }
      });

      setDownloadLinks(response.data.download_links || []);
      setCopyrightNotice(response.data.copyright_notice);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load downloads. Please check your email link.');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-cyan-500 mx-auto mb-4"></div>
          <p className="text-white text-xl">Loading your downloads...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-900 border border-red-500 rounded-xl p-8 text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-white mb-4">Error</h1>
          <p className="text-red-400 mb-6">{error}</p>
          <a
            href="/marketplace"
            className="inline-block px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-bold rounded-lg transition"
          >
            Browse Marketplace
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800">
      <Navigation />
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-xl p-8">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">🎵</div>
              <h1 className="text-3xl font-bold text-white mb-2">Your Music is Ready!</h1>
              <p className="text-gray-400">Thank you for supporting the artist</p>
            </div>

            {/* Copyright Notice */}
            {copyrightNotice && (
              <div className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-6 mb-6">
                <h3 className="text-yellow-400 font-bold mb-2 flex items-center">
                  <span className="text-2xl mr-2">📜</span>
                  Copyright Notice
                </h3>
                <p className="text-yellow-200 text-sm">{copyrightNotice}</p>
              </div>
            )}

            {/* Download Links */}
            <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-6">
              <h2 className="text-xl font-bold text-white mb-4">Download Tracks</h2>
              <div className="space-y-3">
                {downloadLinks.map((link, index) => (
                  <div
                    key={link.track_id || index}
                    className="flex items-center justify-between bg-gray-800 border border-gray-600 rounded-lg p-4"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">🎵</span>
                      <span className="text-white font-medium">{link.title}</span>
                    </div>
                    <a
                      href={`${BACKEND_URL}${link.download_url}`}
                      download
                      className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-bold rounded-lg transition"
                    >
                      Download
                    </a>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm mb-4">
                Save this link! You can return anytime to download your purchase.
              </p>
              <a
                href="/marketplace"
                className="inline-block px-6 py-3 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-cyan-600 hover:to-cyan-700 text-white font-bold rounded-lg transition"
              >
                Browse More Music
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment Success Page
const PaymentSuccess = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setError('No payment session found');
      setLoading(false);
      return;
    }

    // Poll payment status
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.payment_status === 'paid') {
          setPaymentInfo(response.data);
          setLoading(false);
        } else if (attempts >= maxAttempts) {
          setError('Payment verification timed out. Please check your account.');
          setLoading(false);
        } else {
          attempts++;
          setTimeout(poll, pollInterval);
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        setError('Failed to verify payment');
        setLoading(false);
      }
    };

    poll();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-cyan-400 mb-4"></div>
          <p className="text-white text-xl">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-3xl font-bold text-white mb-4">Payment Issue</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/artist/dashboard')}
            className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-bold rounded-lg"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800 flex items-center justify-center">
      <div className="text-center max-w-md bg-gray-900 border border-cyan-500 rounded-2xl p-8">
        <div className="text-6xl mb-4">✅</div>
        <h1 className="text-3xl font-bold text-white mb-4">Payment Successful!</h1>
        <p className="text-gray-400 mb-6">
          {paymentInfo?.payment_type === 'upload_fee' 
            ? 'Your upload fee has been processed. You can now upload music!'
            : 'Your purchase is complete! Enjoy your music in The Vault.'}
        </p>
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <p className="text-gray-400 text-sm">Amount Paid</p>
          <p className="text-cyan-400 text-2xl font-bold">${paymentInfo?.amount?.toFixed(2)}</p>
        </div>
        <button
          onClick={() => navigate(paymentInfo?.payment_type === 'upload_fee' ? '/artist/upload' : '/vault')}
          className="w-full px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-bold rounded-lg"
        >
          {paymentInfo?.payment_type === 'upload_fee' ? 'Upload Music' : 'Go to My Vault'}
        </button>
      </div>
    </div>
  );
};

// Payment Cancel Page
const PaymentCancel = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800 flex items-center justify-center">
      <div className="text-center max-w-md bg-gray-900 border border-gray-700 rounded-2xl p-8">
        <div className="text-6xl mb-4">❌</div>
        <h1 className="text-3xl font-bold text-white mb-4">Payment Cancelled</h1>
        <p className="text-gray-400 mb-6">
          Your payment was cancelled. No charges were made to your account.
        </p>
        <div className="space-y-3">
          <button
            onClick={() => navigate('/artist/dashboard')}
            className="w-full px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-bold rounded-lg"
          >
            Go to Dashboard
          </button>
          <button
            onClick={() => navigate('/marketplace')}
            className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-bold rounded-lg"
          >
            Browse Marketplace
          </button>
        </div>
      </div>
    </div>
  );
};

// Main App
// Animated Splash Screen Component
const SplashScreen = ({ onFinish }) => {
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    // Show logo animation for 3 seconds, then fade out
    const animTimer = setTimeout(() => {
      setFadeOut(true);
    }, 3000);

    // Complete splash after fade out animation (0.5s)
    const finishTimer = setTimeout(() => {
      onFinish();
    }, 3500);

    return () => {
      clearTimeout(animTimer);
      clearTimeout(finishTimer);
    };
  }, [onFinish]);

  const handleSkip = () => {
    onFinish();
  };

  return (
    <div 
      className={`fixed inset-0 z-50 bg-gradient-to-br from-black via-gray-900 to-gray-800 flex items-center justify-center transition-opacity duration-500 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
    >
      {/* Animated Logo */}
      <div className="relative">
        {/* Logo */}
        <img 
          src="/origin-logo.png" 
          alt="Origin Audio" 
          className="h-32 w-auto animate-pulse"
          style={{
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }}
        />
        
        {/* Animated Audio Waves */}
        <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 flex items-end gap-1">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="w-1 bg-cyan-500"
              style={{
                height: '20px',
                animation: `wave 1s ease-in-out infinite`,
                animationDelay: `${i * 0.1}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Skip Button */}
      <button
        onClick={handleSkip}
        className="absolute bottom-8 right-8 px-6 py-3 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-semibold transition border border-white/30 z-10"
      >
        Skip →
      </button>

      {/* CSS Animation */}
      <style>{`
        @keyframes wave {
          0%, 100% { height: 10px; }
          50% { height: 30px; }
        }
      `}</style>
    </div>
  );
};

function App() {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    // Check if splash was already shown in this session
    const splashShown = sessionStorage.getItem('splashShown');
    if (splashShown) {
      setShowSplash(false);
    }
  }, []);

  const handleSplashFinish = () => {
    sessionStorage.setItem('splashShown', 'true');
    setShowSplash(false);
  };

  return (
    <>
      {showSplash && <SplashScreen onFinish={handleSplashFinish} />}
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/ai-marketplace" element={<AIMarketplace />} />
            <Route path="/beats-marketplace" element={<BeatsMarketplace />} />
            <Route path="/album/:id" element={<AlbumDetail />} />
            <Route path="/guest-download" element={<GuestDownload />} />
            <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/payment-cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
            <Route
              path="/vault"
              element={
                <ProtectedRoute requireRole="super_fan">
                  <Vault />
                </ProtectedRoute>
              }
            />
            <Route
              path="/artist/dashboard"
              element={
                <ProtectedRoute requireRole={['artist', 'label', 'ai_creator', 'beat_maker']}>
                  <ArtistDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/artist/upload"
              element={
                <ProtectedRoute requireRole={['artist', 'label', 'ai_creator', 'beat_maker']}>
                  <ArtistUpload />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </>
  );
}

export default App;
